import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {copyText} from './contentGenerator/clipboard';
import {SHOPIFY_STORE_NAME} from './contentGenerator/config';
import {
    C,
    DEFAULT_PROFILE_SECTION_SETTINGS,
    DEFAULT_PROFILES,
    PRODUCT_CATALOG,
    USECASE_OPTIONS,
    delStyle,
    makeRows,
    mergeProfileSettings,
    resolveProfileTone,
    resolveProfileAudience,
    uid,
} from './contentGenerator/constants';
import ProfileSettingsDialog from './contentGenerator/components/ProfileSettingsDialog';
import DescriptionPromptWizard from './contentGenerator/components/DescriptionPromptWizard';
import PromptWizard, {generateAiAngles} from './contentGenerator/components/PromptWizard';
import VarInput from './contentGenerator/components/VarInput';
import VarsPanel from './contentGenerator/components/VarsPanel';
import {
    buildScopedGenerationTasks,
    buildScopedProductGroups,
} from './contentGenerator/generationPlan';
import ChatPanel from './contentReview/components/ChatPanel';
import FieldCard from './contentReview/components/FieldCard';
import {useAI} from './contentGenerator/hooks/useAI';
import {useProfiles} from './contentGenerator/hooks/useProfiles';
import {reconcileProductVars} from './contentGenerator/services/products/productService';
import {pickNextFaqOpening} from './contentGenerator/services/ai/aiService';
import {
    Badge,
    CopyBtn,
    EmptyState,
    FInput,
    FSelect,
    Field,
    Label,
    OutCard,
    Spinner,
} from './contentGenerator/components/ui';
import {
    findHighlightRanges,
    formulaHasHighlight,
    restoreFormulaTextFromHighlights,
} from './contentGenerator/highlightUtils';
import {
    dedupeFormulaTokensByResolvedValue,
    extractFormulaVarNames,
    resolveVars,
    resolveFormula,
    resolveVariableReferenceValue,
    tokenizeFormulaText,
} from './contentGenerator/formulaUtils';
import {exportGeneratedResults} from './shared/exportService';
import {buildProductDetails} from './shared/productDetails';
import {useProductContext} from './shared/ProductContext';
import ProductSelector from './shared/ProductSelector';
import {useProductDetailFieldSettings} from './shared/productDetailFieldSettings';
import ResizeHandle from './shared/ResizeHandle';
import {useResizablePanel} from './shared/useResizablePanel';
import {useReviewAiSettings} from './shared/reviewAiSettings';
import {buildProductGenerationGroups, getProductReviewGroup} from './shared/reviewGrouping';
import {buildBrandContext} from './shared/brand/brandService';
import {trackUsageEvent} from './shared/usageStats';
import {getThemeVars} from './theme';

import {ButtonBase, ClickAwayListener, Popper, Stack} from '@mui/material';
import {ProductDetailFieldsDialog} from './contentReview/components/shared';
import {GroupManagerDialog} from './contentReview/components/Panels';

function ProductGroupPicker({currentProduct, selectedProducts, reviewGroupByProductId, setReviewGroupForProducts}) {
    const [open, setOpen] = useState(false);

    const currentGroup = reviewGroupByProductId[currentProduct?.id] || '';
    if (!currentGroup) return null;

    const items = selectedProducts.map((p) => ({
        id: p.id,
        name: p.name,
        sku: p.sku || p.id,
        category: p.category || '',
        group: reviewGroupByProductId[p.id] || '',
    }));

    return (
        <>
            <span
                onClick={() => setOpen(true)}
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.amber,
                    background: C.amberL,
                    border: `1px solid ${C.amber}33`,
                    padding: '4px 8px',
                    borderRadius: 999,
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    fontFamily: 'monospace',
                    cursor: 'pointer',
                }}>
                {currentGroup}
            </span>
            {open && (
                <GroupManagerDialog
                    items={items}
                    groupKey={currentGroup}
                    onSetGroup={(productId, group) => setReviewGroupForProducts([productId], group)}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
}

function GeneratorExportDropdown({count, onExport}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    return (
        <>
            <button
                ref={ref}
                onClick={() => setOpen((v) => !v)}
                style={{
                    fontSize: 11.5,
                    fontWeight: 500,
                    color: C.teal,
                    background: C.tealL,
                    border: `1px solid ${C.teal}44`,
                    padding: '6px 14px',
                    borderRadius: 7,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                }}>
                Export ({count})
            </button>
            {open && (
                <Popper open anchorEl={ref.current} placement="top-start" style={{zIndex: 1300}}
                    modifiers={[{name: 'offset', options: {offset: [0, 6]}}]}>
                    <ClickAwayListener onClickAway={() => setOpen(false)}>
                        <Stack sx={{
                            background: 'var(--cg-surface)',
                            border: '1px solid var(--cg-border)',
                            borderRadius: '10px',
                            boxShadow: '0 4px 16px var(--cg-shadow)',
                            padding: '4px',
                            minWidth: 150,
                        }}>
                            {[
                                {label: 'XLSX', format: 'xlsx'},
                                {label: 'CSV', format: 'csv'},
                            ].map((opt) => (
                                <ButtonBase
                                    key={opt.format}
                                    onClick={() => { onExport(opt.format); setOpen(false); }}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        fontFamily: 'inherit',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: 'var(--cg-text)',
                                        cursor: 'pointer',
                                        '&:hover': {background: `${C.teal}11`},
                                    }}>
                                    {opt.label}
                                </ButtonBase>
                            ))}
                        </Stack>
                    </ClickAwayListener>
                </Popper>
            )}
        </>
    );
}

const MAX_REFERENCE_IMAGES = 4;
const MAX_REFERENCE_IMAGE_BYTES = 4 * 1024 * 1024;

const cloneReferenceImages = (images = []) => images.map((image) => ({...image}));
const normalizeUniqueVarNames = (names = []) => Array.from(new Set((names || []).filter(Boolean)));
const normalizeGenerationRow = (row = {}) => ({
    ...row,
    uniqueVarNames: normalizeUniqueVarNames(row?.uniqueVarNames),
});
const normalizeGenerationRows = (rows = []) => rows.map((row) => normalizeGenerationRow(row));
const normalizeUniqueVarNamesByIndex = (namesByIndex = []) =>
    (namesByIndex || []).map((names) => normalizeUniqueVarNames(names));
export const normalizeFaqCount = (
    count,
    fallback = DEFAULT_PROFILE_SECTION_SETTINGS.faqs.faqCount,
) => Math.max(1, Math.min(10, Number(count) || fallback));
export const syncRowsToFaqCount = (rows = [], count = 1) => {
    const nextCount = normalizeFaqCount(count);
    const normalizedRows = normalizeGenerationRows(rows);

    if (nextCount > normalizedRows.length) {
        return [
            ...normalizedRows,
            ...Array.from({length: nextCount - normalizedRows.length}, () => ({
                id: uid(),
                field: '',
                kw: '',
                uniqueVarNames: [],
            })),
        ];
    }

    if (nextCount < normalizedRows.length) {
        return normalizedRows.slice(0, nextCount);
    }

    return normalizedRows;
};
export const syncUniqueVarNamesToFaqCount = (namesByIndex = [], count = 1) => {
    const nextCount = normalizeFaqCount(count);
    const normalized = normalizeUniqueVarNamesByIndex(namesByIndex);

    if (nextCount > normalized.length) {
        return [
            ...normalized,
            ...Array.from({length: nextCount - normalized.length}, () => []),
        ];
    }

    if (nextCount < normalized.length) {
        return normalized.slice(0, nextCount);
    }

    return normalized;
};
const formatVarLabel = (name = '') =>
    String(name)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
const extractSelectableFormulaVarNames = (texts = []) =>
    Array.from(new Set(texts.flatMap((text) => extractFormulaVarNames(text))));
const toggleUniqueVarName = (names = [], varName = '') =>
    names.includes(varName) ? names.filter((name) => name !== varName) : [...names, varName];

const countGeneratedOutput = (result = {}) => ({
    titles: result?.title ? 1 : 0,
    descriptions: result?.desc ? 1 : 0,
    bullets: Array.isArray(result?.bullets)
        ? result.bullets.filter((bullet) => bullet?.text).length
        : 0,
    faqs: Array.isArray(result?.faqs)
        ? result.faqs.filter((faq) => faq?.question && faq?.answer).length
        : 0,
});

export const applySharedSubgroupSelectionsToDraft = (
    draft = {},
    {
        titleUniqueVarNames = [],
        descriptionUniqueVarNames = [],
        bulletUniqueVarNamesByIndex = [],
    } = {},
) => ({
    ...draft,
    titleUniqueVarNames: normalizeUniqueVarNames(titleUniqueVarNames),
    descriptionUniqueVarNames: normalizeUniqueVarNames(descriptionUniqueVarNames),
    rows: normalizeGenerationRows(
        (draft.rows || []).map((row, index) => ({
            ...row,
            uniqueVarNames: normalizeUniqueVarNames(bulletUniqueVarNamesByIndex[index] || []),
        })),
    ),
});

const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
        reader.readAsDataURL(file);
    });

const sourceHighlightTextStyle = {
    background: 'rgba(26,122,74,.12)',
    border: '1px solid rgba(26,122,74,.25)',
    borderRadius: 4,
    padding: '0 3px',
    WebkitBoxDecorationBreak: 'clone',
    boxDecorationBreak: 'clone',
};

const renderSourceHighlights = (text, highlightSubstrings = [], keyPrefix = '') => {
    const ranges = findHighlightRanges(text, highlightSubstrings);
    if (!ranges.length) return [<span key={`${keyPrefix}plain`}>{text}</span>];

    const parts = [];
    let cursor = 0;

    ranges.forEach((range, index) => {
        if (cursor < range.start) {
            parts.push(
                <span key={`${keyPrefix}t-${index}`}>{text.slice(cursor, range.start)}</span>,
            );
        }

        parts.push(
            <span
                key={`${keyPrefix}h-${index}`}
                title={range.token.original || range.token.resolved}
                style={sourceHighlightTextStyle}>
                {text.slice(range.start, range.end)}
            </span>,
        );
        cursor = range.end;
    });

    if (cursor < text.length) {
        parts.push(<span key={`${keyPrefix}tail`}>{text.slice(cursor)}</span>);
    }

    return parts;
};

const getEditModeText = (text, highlightSubstrings = []) =>
    restoreFormulaTextFromHighlights(text, highlightSubstrings);

const cloneFieldSubgroupMeta = (meta = null) => {
    if (!meta) return null;

    return {
        uniqueVarNames: [...(meta.uniqueVarNames || [])],
        groupKey: meta.groupKey || '',
        valuesByName: {...(meta.valuesByName || {})},
    };
};

const buildFieldSubgroupMeta = (
    uniqueVarNames = [],
    baseVars = [],
    product = null,
    managedProductVarNames = [],
) => {
    const normalizedUniqueVarNames = normalizeUniqueVarNames(uniqueVarNames);
    if (!normalizedUniqueVarNames.length) return null;

    const mergedVars = reconcileProductVars(
        baseVars,
        product?.vars || [],
        managedProductVarNames,
    );
    const valuesByName = Object.fromEntries(
        normalizedUniqueVarNames.map((name) => [
            name,
            resolveVariableReferenceValue(name, mergedVars).value || '',
        ]),
    );

    return {
        uniqueVarNames: normalizedUniqueVarNames,
        groupKey: normalizedUniqueVarNames
            .map((name) => `${name}:${valuesByName[name] || ''}`)
            .join('|'),
        valuesByName,
    };
};

const cloneFieldDependencyMeta = (meta = null) => {
    if (!meta) return null;

    return {
        source: meta.source || 'generated',
        contextVarNames: [...(meta.contextVarNames || [])],
        templateValuesByName: {...(meta.templateValuesByName || {})},
        templateProductId: meta.templateProductId || '',
        templateSku: meta.templateSku || '',
    };
};

const buildFieldDependencyMeta = (
    contextVarNames = [],
    baseVars = [],
    product = null,
    managedProductVarNames = [],
) => {
    const normalizedContextVarNames = normalizeUniqueVarNames(contextVarNames);
    if (!normalizedContextVarNames.length) return null;

    const mergedVars = reconcileProductVars(
        baseVars,
        product?.vars || [],
        managedProductVarNames,
    );
    const templateValuesByName = Object.fromEntries(
        normalizedContextVarNames.map((name) => [
            name,
            resolveVariableReferenceValue(name, mergedVars).value || '',
        ]),
    );

    return {
        source: 'generated',
        contextVarNames: normalizedContextVarNames,
        templateValuesByName,
        templateProductId: product?.id || '',
        templateSku: product?.sku || product?.id || '',
    };
};

const buildFieldDependencyMetaFromTexts = (
    texts = [],
    baseVars = [],
    product = null,
    managedProductVarNames = [],
) =>
    buildFieldDependencyMeta(
        texts.flatMap((text) => extractFormulaVarNames(text)),
        baseVars,
        product,
        managedProductVarNames,
    );

// Bullet generation should not inherit the full topic, which commonly
// includes product-name context that varies across siblings in a group.
// Keep bullet dependencies scoped to the row's own field/keywords instead.
const buildBulletFieldDependencyMeta = (
    row = {},
    baseVars = [],
    product = null,
    managedProductVarNames = [],
) =>
    buildFieldDependencyMetaFromTexts(
        [row?.field || '', row?.kw || ''],
        baseVars,
        product,
        managedProductVarNames,
    );

const buildGeneratedFieldDependencies = ({
    draft = {},
    baseVars = [],
    templateProduct = null,
    managedProductVarNames = [],
    enabledState = {},
}) => ({
    title: enabledState?.title
        ? buildFieldDependencyMetaFromTexts(
              [draft?.topic || ''],
              baseVars,
              templateProduct,
              managedProductVarNames,
          )
        : null,
    description: enabledState?.desc
        ? buildFieldDependencyMetaFromTexts(
              [draft?.topic || '', draft?.descriptionContext || ''],
              baseVars,
              templateProduct,
              managedProductVarNames,
          )
        : null,
    bullets: enabledState?.bullets
        ? (draft?.rows || []).map((row) =>
              buildBulletFieldDependencyMeta(
                  row,
                  baseVars,
                  templateProduct,
                  managedProductVarNames,
              ),
          )
        : [],
    faqs: enabledState?.faqs
        ? (draft?.rows || []).map((row) =>
              buildBulletFieldDependencyMeta(
                  row,
                  baseVars,
                  templateProduct,
                  managedProductVarNames,
              ),
          )
        : [],
});

const cloneGeneratedResult = (generatedResult = {}) => ({
    ...generatedResult,
    bullets: Array.isArray(generatedResult?.bullets)
        ? generatedResult.bullets.map((bullet) => ({...bullet}))
        : [],
    faqs: Array.isArray(generatedResult?.faqs)
        ? generatedResult.faqs.map((faq) => ({...faq}))
        : [],
    generationHighlights: {
        title: [...(generatedResult?.generationHighlights?.title || [])],
        description: [...(generatedResult?.generationHighlights?.description || [])],
        bullets: Array.isArray(generatedResult?.generationHighlights?.bullets)
            ? generatedResult.generationHighlights.bullets.map((highlightGroup) => [
              ...highlightGroup,
          ])
        : [],
        faqs: Array.isArray(generatedResult?.generationHighlights?.faqs)
            ? generatedResult.generationHighlights.faqs.map((highlightGroup) => [
                  ...highlightGroup,
              ])
            : [],
    },
    fieldSubgroups: {
        title: cloneFieldSubgroupMeta(generatedResult?.fieldSubgroups?.title),
        description: cloneFieldSubgroupMeta(generatedResult?.fieldSubgroups?.description),
        bullets: Array.isArray(generatedResult?.fieldSubgroups?.bullets)
            ? generatedResult.fieldSubgroups.bullets.map((meta) => cloneFieldSubgroupMeta(meta))
            : [],
        faqs: Array.isArray(generatedResult?.fieldSubgroups?.faqs)
            ? generatedResult.fieldSubgroups.faqs.map((meta) => cloneFieldSubgroupMeta(meta))
            : [],
    },
    fieldDependencies: {
        title: cloneFieldDependencyMeta(generatedResult?.fieldDependencies?.title),
        description: cloneFieldDependencyMeta(generatedResult?.fieldDependencies?.description),
        bullets: Array.isArray(generatedResult?.fieldDependencies?.bullets)
            ? generatedResult.fieldDependencies.bullets.map((meta) =>
                  cloneFieldDependencyMeta(meta),
              )
            : [],
        faqs: Array.isArray(generatedResult?.fieldDependencies?.faqs)
            ? generatedResult.fieldDependencies.faqs.map((meta) =>
                  cloneFieldDependencyMeta(meta),
              )
            : [],
    },
});

const buildEmptyGeneratedResult = (draft, enabledState = {}) => ({
    title: '',
    desc: '',
    bullets: enabledState?.bullets
        ? (draft?.rows || []).map((row) => ({
              field: row?.field || '',
              text: '',
          }))
        : [],
    faqs: enabledState?.faqs
        ? (draft?.rows || []).map(() => ({
              question: '',
              answer: '',
          }))
        : [],
    generationHighlights: {
        title: [],
        description: [],
        bullets: enabledState?.bullets ? (draft?.rows || []).map(() => []) : [],
        faqs: enabledState?.faqs ? (draft?.rows || []).map(() => []) : [],
    },
    fieldSubgroups: {
        title: null,
        description: null,
        bullets: enabledState?.bullets ? (draft?.rows || []).map(() => null) : [],
        faqs: enabledState?.faqs ? (draft?.rows || []).map(() => null) : [],
    },
    fieldDependencies: {
        title: null,
        description: null,
        bullets: enabledState?.bullets ? (draft?.rows || []).map(() => null) : [],
        faqs: enabledState?.faqs ? (draft?.rows || []).map(() => null) : [],
    },
});

export const ResolvedText = ({text, vars, highlightSubstrings = []}) => {
    if (!text) return null;
    const normalizedText = highlightSubstrings.length
        ? restoreFormulaTextFromHighlights(text, highlightSubstrings)
        : text;
    if (!normalizedText.includes('{{') && !highlightSubstrings.length) return normalizedText;

    const segments = normalizedText.includes('{{')
        ? tokenizeFormulaText(normalizedText)
        : [{type: 'text', value: normalizedText}];

    return segments.flatMap((segment, index) => {
        if (!segment?.value) return null;

        if (segment.type === 'formula') {
            const expression = segment.value.slice(2, -2).trim();
            const resolved = resolveFormula(expression, vars);

            return (
                <span
                    key={index}
                    title={segment.value}
                    style={{
                        background: 'rgba(26,122,74,.12)',
                        border: '1px solid rgba(26,122,74,.25)',
                        borderRadius: 4,
                        padding: '0 3px',
                        ...(formulaHasHighlight(segment.value, resolved, highlightSubstrings)
                            ? sourceHighlightTextStyle
                            : {}),
                    }}>
                    {resolved}
                </span>
            );
        }

        return renderSourceHighlights(segment.value, highlightSubstrings, `resolved-${index}-`);
    });
};

const SubgroupAttributeSelector = ({
    label,
    optionNames = [],
    selectedNames = [],
    onToggle,
    emptyLabel,
}) => {
    if (!optionNames.length) {
        return (
            <div style={{fontSize: 9.5, color: C.textMuted, lineHeight: 1.5}}>
                {emptyLabel}
            </div>
        );
    }

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
            <div
                style={{
                    fontSize: 9.5,
                    color: C.textMuted,
                    lineHeight: 1.5,
                }}>
                {label}
            </div>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                {optionNames.map((name) => {
                    const isSelected = selectedNames.includes(name);

                    return (
                        <button
                            key={name}
                            type="button"
                            onClick={() => onToggle(name)}
                            style={{
                                fontFamily: 'inherit',
                                fontSize: 10.5,
                                fontWeight: 600,
                                padding: '4px 8px',
                                borderRadius: 999,
                                border: `1px solid ${isSelected ? `${C.amber}66` : C.border}`,
                                background: isSelected ? C.amberL : C.surfaceRaised,
                                color: isSelected ? C.amber : C.textSoft,
                                cursor: 'pointer',
                            }}>
                            {formatVarLabel(name)}
                            {isSelected ? ' subgroup' : ''}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const formatSubgroupSummary = (selectedNames = []) => {
    if (!selectedNames.length) return 'Shared across group';
    if (selectedNames.length === 1) return `Split by ${formatVarLabel(selectedNames[0])}`;
    return `Split by ${formatVarLabel(selectedNames[0])} +${selectedNames.length - 1}`;
};

const BulletSubgroupControl = ({
    optionNames = [],
    selectedNames = [],
    open = false,
    onToggleOpen,
    onToggle,
    onClear,
}) => {
    if (!optionNames.length && !selectedNames.length) return null;

    const isActive = selectedNames.length > 0;

    return (
        <div style={{marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6}}>
            <button
                type="button"
                onClick={onToggleOpen}
                aria-expanded={open}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    width: '100%',
                    padding: '6px 9px',
                    borderRadius: 10,
                    border: `1px solid ${open || isActive ? `${C.amber}55` : C.border}`,
                    background: open || isActive ? C.amberL : C.surfaceRaised,
                    color: open || isActive ? C.amber : C.textSoft,
                    fontFamily: 'inherit',
                    fontSize: 10.5,
                    cursor: 'pointer',
                }}>
                <span style={{fontWeight: 700, letterSpacing: '.02em'}}>Variation</span>
                <span
                    style={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 500,
                    }}>
                    {formatSubgroupSummary(selectedNames)}
                </span>
                <span style={{fontSize: 10, flexShrink: 0}}>{open ? '▴' : '▾'}</span>
            </button>

            {open && (
                <div
                    style={{
                        border: `1px solid ${C.border}`,
                        borderRadius: 10,
                        background: C.surfaceAlt,
                        padding: '9px 10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                    }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                        }}>
                        <div style={{fontSize: 9.5, color: C.textMuted, lineHeight: 1.5}}>
                            Choose which used attributes should create separate versions of this
                            bullet inside the group.
                        </div>
                        {selectedNames.length > 0 && (
                            <button
                                type="button"
                                onClick={onClear}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: C.textSoft,
                                    fontFamily: 'inherit',
                                    fontSize: 10,
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                }}>
                                Clear
                            </button>
                        )}
                    </div>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                        {optionNames.map((name) => {
                            const isSelected = selectedNames.includes(name);

                            return (
                                <button
                                    key={name}
                                    type="button"
                                    onClick={() => onToggle(name)}
                                    style={{
                                        fontFamily: 'inherit',
                                        fontSize: 10.5,
                                        fontWeight: 600,
                                        padding: '4px 8px',
                                        borderRadius: 999,
                                        border: `1px solid ${isSelected ? `${C.amber}66` : C.border}`,
                                        background: isSelected ? C.amberL : C.surfaceRaised,
                                        color: isSelected ? C.amber : C.textSoft,
                                        cursor: 'pointer',
                                    }}>
                                    {formatVarLabel(name)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default function ContentGenerator({apiKey = '', brandProfile = null, theme = 'light', onWorkspaceChange}) {
    const defaultProduct = PRODUCT_CATALOG[0];
    const defaultProfile = DEFAULT_PROFILES[0];

    // ── Product context (shared with ContentReview) ──
    const {
        collectionId,
        productsError,
        productSource,
        currentProductIndex,
        setCurrentProductIndex,
        selectedProducts,
        currentProduct,
        vars,
        setVars,
        managedProductVarNames,
        setActiveProfileSnapshot,
        reviewGroupByProductId,
        setReviewGroupForProducts,
        resultsByProductId,
        setResultsByProductId,
        clearAllResults,
    } = useProductContext();

    const [activeProfileId, setActiveProfileId] = useState(defaultProfile.id);
    const [profileDialogState, setProfileDialogState] = useState({
        open: false,
        profile: null,
    });
    const [profileActionPending, setProfileActionPending] = useState(false);
    const [topic, setTopic] = useState(defaultProduct.topic);
    const [usecase, setUsecase] = useState(defaultProfile.settings.usecase);
    const [enabled, setEnabled] = useState(defaultProfile.settings.enabled);
    const [rows, setRows] = useState(normalizeGenerationRows(makeRows(defaultProduct.fields)));
    // Number of FAQs to generate. Seeded from the active profile so the
    // user's previous preference survives a reload; updates to the
    // stepper drive `rows` length when FAQs are the active section.
    const [faqCount, setFaqCount] = useState(
        normalizeFaqCount(defaultProfile.settings.faqs?.faqCount),
    );
    const [suggestingFaqTopics, setSuggestingFaqTopics] = useState(false);
    const [activeWizard, setActiveWizard] = useState(null);
    const [openBulletSubgroupRowId, setOpenBulletSubgroupRowId] = useState(null);
    const [uiError, setUiError] = useState('');
    const [copied, setCopied] = useState({});
    const [editingField, setEditingField] = useState(null);
    const [resultFieldMeta, setResultFieldMeta] = useState({});
    const [chatFieldId, setChatFieldId] = useState(null);
    const [chatHistories, setChatHistories] = useState({});
    const [descriptionContext, setDescriptionContext] = useState('');
    const [titleUniqueVarNames, setTitleUniqueVarNames] = useState([]);
    const [descriptionUniqueVarNames, setDescriptionUniqueVarNames] = useState([]);
    const [bulletUniqueVarNamesByIndex, setBulletUniqueVarNamesByIndex] = useState([]);
    const [includeProductImage, setIncludeProductImage] = useState(
        Boolean(defaultProduct.imageUrl),
    );
    const [referenceImages, setReferenceImages] = useState([]);
    const [draftsByProductId, setDraftsByProductId] = useState({});
    const [showDescriptionWizard, setShowDescriptionWizard] = useState(false);
    const [focusedGeneratorInput, setFocusedGeneratorInput] = useState('');
    const [focusedBulletRowId, setFocusedBulletRowId] = useState(null);
    const [batchGenerationState, setBatchGenerationState] = useState(null);
    const [productFieldConfigOpen, setProductFieldConfigOpen] = useState(false);
    const productDetailFieldSettings = useProductDetailFieldSettings();
    const {
        customActions,
        customCommands,
        setCustomActions,
    } = useReviewAiSettings();
    const appliedProductRef = useRef('');
    const imagePickerInputRef = useRef(null);
    const generatorRailRef = useRef(null);
    const [generatorRailFrame, setGeneratorRailFrame] = useState({left: 0, width: 0});
    const {
        width: generatorRailWidth,
        startDrag: startGeneratorRailDrag,
    } = useResizablePanel({
        storageKey: 'cg-rail-width',
        defaultWidth: 460,
        min: 360,
        max: 720,
    });
    const sharedSubgroupSelectionsRef = useRef({
        titleUniqueVarNames: [],
        descriptionUniqueVarNames: [],
        bulletUniqueVarNamesByIndex: [],
    });
    const {
        profiles,
        isLoading: profilesLoading,
        error: profilesError,
        source: profilesSource,
        saveProfile,
        deleteProfile,
    } = useProfiles(SHOPIFY_STORE_NAME);
    const {
        generateAiContent,
        loading: aiLoading,
        error: aiError,
        clearError: clearAiError,
    } = useAI();

    useEffect(() => {
        const rail = generatorRailRef.current;
        if (!rail || typeof window === 'undefined') return undefined;

        const updateRailFrame = () => {
            const rect = rail.getBoundingClientRect();
            setGeneratorRailFrame((currentFrame) => {
                const nextFrame = {
                    left: Math.round(rect.left),
                    width: Math.round(rect.width),
                };
                return currentFrame.left === nextFrame.left &&
                    currentFrame.width === nextFrame.width
                    ? currentFrame
                    : nextFrame;
            });
        };

        updateRailFrame();

        const resizeObserver =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(updateRailFrame)
                : null;
        resizeObserver?.observe(rail);

        window.addEventListener('resize', updateRailFrame);
        window.addEventListener('scroll', updateRailFrame, true);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateRailFrame);
            window.removeEventListener('scroll', updateRailFrame, true);
        };
    }, []);

    const activeProfile = profiles.find((profile) => profile.id === activeProfileId);
    const tone = useMemo(() => resolveProfileTone(activeProfile?.settings, brandProfile), [activeProfile, brandProfile]);
    const audience = useMemo(() => resolveProfileAudience(activeProfile?.settings, brandProfile), [activeProfile, brandProfile]);
    const currentResult = currentProduct ? resultsByProductId[currentProduct.id] || null : null;
    const currentGenerationHighlights = currentResult?.generationHighlights || {
        title: [],
        description: [],
        bullets: [],
        faqs: [],
    };
    const currentProductDetails = buildProductDetails(
        currentProduct,
        productDetailFieldSettings,
    );
    const sharedSubgroupSelections = useMemo(
        () => ({
            titleUniqueVarNames,
            descriptionUniqueVarNames,
            bulletUniqueVarNamesByIndex: normalizeUniqueVarNamesByIndex(
                bulletUniqueVarNamesByIndex,
            ),
        }),
        [bulletUniqueVarNamesByIndex, descriptionUniqueVarNames, titleUniqueVarNames],
    );
    const selectedProductsById = useMemo(
        () => new Map(selectedProducts.map((product) => [product.id, product])),
        [selectedProducts],
    );
    const currentProductReviewGroup = useMemo(
        () =>
            getProductReviewGroup({
                product: currentProduct,
                selectedProducts,
                reviewGroupByProductId,
            }),
        [currentProduct, reviewGroupByProductId, selectedProducts],
    );

    useEffect(() => {
        sharedSubgroupSelectionsRef.current = sharedSubgroupSelections;
    }, [sharedSubgroupSelections]);

    const productGenerationGroups = useMemo(
        () =>
            buildProductGenerationGroups({
                selectedProducts,
                reviewGroupByProductId,
                preferredProductId: currentProduct?.id || '',
            }),
        [currentProduct?.id, reviewGroupByProductId, selectedProducts],
    );
    const currentProductGenerationGroup = useMemo(
        () =>
            productGenerationGroups.find((group) =>
                group.products.some((product) => product.id === currentProduct?.id),
            ) || null,
        [currentProduct?.id, productGenerationGroups],
    );
    const currentResultTargetProductIds = useMemo(
        () =>
            currentProductGenerationGroup?.products?.length
                ? currentProductGenerationGroup.products.map((product) => product.id)
                : currentProduct?.id
                  ? [currentProduct.id]
                  : [],
        [currentProduct?.id, currentProductGenerationGroup],
    );
    const activeReferenceImages = useMemo(() => {
        const productImages =
            includeProductImage && currentProduct?.imageUrl
                ? [
                      {
                          id: `${currentProduct.id}-catalog-image`,
                          name: `${currentProduct.name || currentProduct.sku} catalog image`,
                          url: currentProduct.imageUrl,
                          source: 'catalog',
                      },
                  ]
                : [];

        return [...productImages, ...referenceImages];
    }, [currentProduct, includeProductImage, referenceImages]);
    const themeVars = useMemo(() => getThemeVars(theme), [theme]);
    const isDirty = activeProfile
        ? activeProfile.settings.usecase !== usecase ||
          activeProfile.settings.enabled.title !== enabled.title ||
          activeProfile.settings.enabled.desc !== enabled.desc ||
          activeProfile.settings.enabled.bullets !== enabled.bullets ||
          activeProfile.settings.enabled.faqs !== enabled.faqs ||
          (enabled.faqs &&
              normalizeFaqCount(activeProfile.settings?.faqs?.faqCount) !==
                  normalizeFaqCount(faqCount))
        : false;
    const buildGeneratorDraft = useCallback(
        (product) => ({
            topic: product?.topic || defaultProduct.topic,
            titleUniqueVarNames: [],
            rows: normalizeGenerationRows(
                makeRows(product?.fields?.length ? product.fields : defaultProduct.fields),
            ),
            descriptionUniqueVarNames: [],
            includeProductImage: Boolean(product?.imageUrl),
            referenceImages: [],
        }),
        [defaultProduct.fields, defaultProduct.topic],
    );

    const buildProfileSettings = useCallback(
        (baseSettings = {}, {useCurrentGeneratorSettings = false} = {}) => {
            const mergedSettings = mergeProfileSettings(baseSettings);

            return {
                ...mergedSettings,
                tone: useCurrentGeneratorSettings ? tone : mergedSettings.tone,
                usecase: useCurrentGeneratorSettings ? usecase : mergedSettings.usecase,
                audience: useCurrentGeneratorSettings ? audience : mergedSettings.audience,
                enabled: useCurrentGeneratorSettings ? {...enabled} : {...mergedSettings.enabled},
                faqs: {
                    ...mergedSettings.faqs,
                    faqCount: useCurrentGeneratorSettings
                        ? normalizeFaqCount(faqCount)
                        : normalizeFaqCount(mergedSettings.faqs?.faqCount),
                },
            };
        },
        [audience, enabled, faqCount, tone, usecase],
    );

    const syncCurrentDraft = useCallback(
        (patch = {}) => {
            if (!currentProduct?.id) return;

            setDraftsByProductId((currentDrafts) => ({
                ...currentDrafts,
                [currentProduct.id]: {
                    topic: patch.topic ?? topic,
                    titleUniqueVarNames: normalizeUniqueVarNames(
                        patch.titleUniqueVarNames ?? titleUniqueVarNames,
                    ),
                    rows: normalizeGenerationRows(
                        (patch.rows ?? rows).map((row) => ({...row})),
                    ),
                    descriptionUniqueVarNames: normalizeUniqueVarNames(
                        patch.descriptionUniqueVarNames ?? descriptionUniqueVarNames,
                    ),
                    includeProductImage: Boolean(
                        (patch.includeProductImage ?? includeProductImage) &&
                            currentProduct.imageUrl,
                    ),
                    referenceImages: cloneReferenceImages(
                        patch.referenceImages ?? referenceImages,
                    ),
                },
            }));
        },
        [
            currentProduct,
            descriptionUniqueVarNames,
            includeProductImage,
            referenceImages,
            rows,
            titleUniqueVarNames,
            topic,
        ],
    );

    const handleTopicChange = useCallback((value) => {
        setTopic(value);
        syncCurrentDraft({topic: value});
    }, [syncCurrentDraft]);

    // descriptionContext is global session state — applies to every
    // product in the generator, no per-product persistence.
    const handleDescriptionContextChange = useCallback((value) => {
        setDescriptionContext(value);
    }, []);

    const titleSelectableVarNames = useMemo(
        () => extractSelectableFormulaVarNames([topic]),
        [topic],
    );
    const descriptionSelectableVarNames = useMemo(
        () => extractSelectableFormulaVarNames([topic, descriptionContext]),
        [descriptionContext, topic],
    );

    const handleTitleUniqueVarToggle = useCallback((varName) => {
        setTitleUniqueVarNames((currentNames) =>
            normalizeUniqueVarNames(toggleUniqueVarName(currentNames, varName)),
        );
    }, []);

    const handleDescriptionUniqueVarToggle = useCallback((varName) => {
        setDescriptionUniqueVarNames((currentNames) =>
            normalizeUniqueVarNames(toggleUniqueVarName(currentNames, varName)),
        );
    }, []);

    useEffect(() => {
        setTitleUniqueVarNames((currentNames) => {
            const nextNames = currentNames.filter((name) => titleSelectableVarNames.includes(name));
            return nextNames.length === currentNames.length ? currentNames : nextNames;
        });
    }, [titleSelectableVarNames]);

    useEffect(() => {
        setDescriptionUniqueVarNames((currentNames) => {
            const nextNames = currentNames.filter((name) =>
                descriptionSelectableVarNames.includes(name),
            );
            return nextNames.length === currentNames.length ? currentNames : nextNames;
        });
    }, [descriptionSelectableVarNames]);

    const openReferenceImagePicker = useCallback(() => {
        imagePickerInputRef.current?.click();
    }, []);

    const handleReferenceImageUpload = useCallback(
        async (event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = '';

            if (!files.length) return;

            const availableSlots = Math.max(0, MAX_REFERENCE_IMAGES - referenceImages.length);
            if (!availableSlots) {
                setUiError(
                    `You can attach up to ${MAX_REFERENCE_IMAGES} extra reference images per SKU.`,
                );
                return;
            }

            const limitedFiles = files.slice(0, availableSlots);
            const readableFiles = limitedFiles.filter(
                (file) =>
                    (!file.type || file.type.startsWith('image/')) &&
                    file.size <= MAX_REFERENCE_IMAGE_BYTES,
            );
            const invalidCount = limitedFiles.length - readableFiles.length;

            try {
                const nextImages = await Promise.all(
                    readableFiles.map(async (file) => ({
                        id: uid(),
                        name: file.name,
                        url: await readFileAsDataUrl(file),
                        source: 'upload',
                    })),
                );

                if (nextImages.length) {
                    setReferenceImages((currentImages) => {
                        const updatedImages = [...currentImages, ...nextImages];
                        syncCurrentDraft({referenceImages: updatedImages});
                        return updatedImages;
                    });
                }

                const warnings = [];
                if (files.length > availableSlots) {
                    warnings.push(
                        `Only ${availableSlots} image${availableSlots === 1 ? '' : 's'} were added because each SKU is limited to ${MAX_REFERENCE_IMAGES} uploaded references.`,
                    );
                }
                if (invalidCount) {
                    warnings.push('Non-image files or images over 4 MB were skipped.');
                }

                setUiError(warnings.join(' '));
            } catch (uploadError) {
                setUiError(uploadError.message || 'Could not read the selected image.');
            }
        },
        [referenceImages.length, syncCurrentDraft],
    );

    const removeReferenceImage = useCallback((imageId) => {
        setReferenceImages((currentImages) => {
            const updatedImages = currentImages.filter((image) => image.id !== imageId);
            syncCurrentDraft({referenceImages: updatedImages});
            return updatedImages;
        });
    }, [syncCurrentDraft]);

    const setRowsWithDraft = useCallback((updater) => {
        setRows((currentRows) => {
            const nextRows = normalizeGenerationRows(
                typeof updater === 'function' ? updater(currentRows) : updater,
            );
            syncCurrentDraft({rows: nextRows});
            return nextRows;
        });
    }, [syncCurrentDraft]);

    const applyProfile = useCallback(
        (profile) => {
            if (!profile) return;
            const profileFaqCount = normalizeFaqCount(profile.settings?.faqs?.faqCount);
            setActiveProfileSnapshot(profile);
            setActiveProfileId(profile.id);
            setUsecase(profile.settings.usecase);
            setEnabled({...profile.settings.enabled});
            setFaqCount(profileFaqCount);
            if (profile.settings.enabled?.faqs) {
                setRowsWithDraft((currentRows) =>
                    syncRowsToFaqCount(currentRows, profileFaqCount),
                );
                setBulletUniqueVarNamesByIndex((currentNames) =>
                    syncUniqueVarNamesToFaqCount(currentNames, profileFaqCount),
                );
            }
            clearAllResults();
            setUiError('');
            clearAiError();
        },
        [clearAiError, clearAllResults, setActiveProfileSnapshot, setRowsWithDraft],
    );

    const revertProfile = () => {
        if (!activeProfile) return;
        const profileFaqCount = normalizeFaqCount(activeProfile.settings?.faqs?.faqCount);
        setUsecase(activeProfile.settings.usecase);
        setEnabled({...activeProfile.settings.enabled});
        setFaqCount(profileFaqCount);
        if (activeProfile.settings.enabled?.faqs) {
            setRowsWithDraft((currentRows) =>
                syncRowsToFaqCount(currentRows, profileFaqCount),
            );
            setBulletUniqueVarNamesByIndex((currentNames) =>
                syncUniqueVarNamesToFaqCount(currentNames, profileFaqCount),
            );
        }
    };

    const updateResult = (patch, targetProductIds = currentResultTargetProductIds) => {
        if (!targetProductIds.length) return;
        setResultsByProductId((prev) => ({
            ...prev,
            ...Object.fromEntries(
                targetProductIds.map((productId) => [
                    productId,
                    {...(prev[productId] || {}), ...patch},
                ]),
            ),
        }));
    };

    const updateBullet = (index, patch, targetProductIds = currentResultTargetProductIds) => {
        if (!targetProductIds.length) return;
        setResultsByProductId((prev) => {
            return {
                ...prev,
                ...Object.fromEntries(
                    targetProductIds.map((productId) => {
                        const result = prev[productId] || {};
                        const bullets = [...(result.bullets || [])];
                        bullets[index] = {...bullets[index], ...patch};
                        return [productId, {...result, bullets}];
                    }),
                ),
            };
        });
    };

    const updateFaq = (index, patch, targetProductIds = currentResultTargetProductIds) => {
        if (!targetProductIds.length) return;
        setResultsByProductId((prev) => ({
            ...prev,
            ...Object.fromEntries(
                targetProductIds.map((productId) => {
                    const result = prev[productId] || {};
                    const faqs = [...(result.faqs || [])];
                    faqs[index] = {...faqs[index], ...patch};
                    return [productId, {...result, faqs}];
                }),
            ),
        }));
    };

    const getDraftForProduct = useCallback(
        (product) => {
            if (!product) {
                return applySharedSubgroupSelectionsToDraft(
                    buildGeneratorDraft(product),
                    sharedSubgroupSelections,
                );
            }

            let draft;
            if (product.id === currentProduct?.id) {
                draft = {
                    topic,
                    rows: normalizeGenerationRows(rows.map((row) => ({...row}))),
                    descriptionContext,
                    includeProductImage,
                    referenceImages: cloneReferenceImages(referenceImages),
                };
            } else {
                const storedDraft = draftsByProductId[product.id] || buildGeneratorDraft(product);

                draft = {
                    topic: storedDraft.topic,
                    rows: normalizeGenerationRows(rows.map((row) => ({...row}))),
                    descriptionContext,
                    includeProductImage: Boolean(storedDraft.includeProductImage),
                    referenceImages: cloneReferenceImages(storedDraft.referenceImages),
                };
            }

            return applySharedSubgroupSelectionsToDraft(draft, sharedSubgroupSelections);
        },
        [
            buildGeneratorDraft,
            bulletUniqueVarNamesByIndex,
            currentProduct?.id,
            descriptionContext,
            descriptionUniqueVarNames,
            draftsByProductId,
            includeProductImage,
            referenceImages,
            rows,
            titleUniqueVarNames,
            topic,
            sharedSubgroupSelections,
        ],
    );

    const getUniqueVarNamesForField = useCallback(
        (fieldType, {bulletIndex = -1, faqIndex = -1} = {}) => {
            if (fieldType === 'title') {
                return normalizeUniqueVarNames(titleUniqueVarNames);
            }

            if (fieldType === 'description') {
                return normalizeUniqueVarNames(descriptionUniqueVarNames);
            }

            if (fieldType === 'bullet') {
                return normalizeUniqueVarNames(bulletUniqueVarNamesByIndex[bulletIndex]);
            }

            if (fieldType === 'faq') {
                return normalizeUniqueVarNames(bulletUniqueVarNamesByIndex[faqIndex]);
            }

            return [];
        },
        [bulletUniqueVarNamesByIndex, descriptionUniqueVarNames, titleUniqueVarNames],
    );

    const getFieldTargetProductIds = useCallback(
        (fieldType, {bulletIndex = -1, faqIndex = -1} = {}) => {
            const fallbackIds =
                currentResultTargetProductIds.length > 0
                    ? currentResultTargetProductIds
                    : currentProduct?.id
                      ? [currentProduct.id]
                      : [];

            if (
                !currentProduct?.id ||
                !currentProductGenerationGroup?.products?.length ||
                currentProductGenerationGroup.products.length < 2
            ) {
                return fallbackIds;
            }

            const uniqueVarNames = getUniqueVarNamesForField(fieldType, {
                bulletIndex,
                faqIndex,
            });
            if (!uniqueVarNames.length) return fallbackIds;

            const scopedGroups = buildScopedProductGroups({
                products: currentProductGenerationGroup.products,
                baseVars: vars,
                managedProductVarNames,
                uniqueVarNames,
                preferredProductId: currentProduct.id,
            });
            const activeScopedGroup = scopedGroups.find((group) =>
                group.products.some((product) => product.id === currentProduct.id),
            );

            return activeScopedGroup?.products?.length
                ? activeScopedGroup.products.map((product) => product.id)
                : fallbackIds;
        },
        [
            currentProduct?.id,
            currentProductGenerationGroup,
            currentResultTargetProductIds,
            getUniqueVarNamesForField,
            managedProductVarNames,
            vars,
        ],
    );

    const handleResultFieldUpdate = (fieldId, patch) => {
        if (patch.text !== undefined) {
            if (fieldId === 'gen-title') {
                updateResult({title: patch.text}, getFieldTargetProductIds('title'));
            } else if (fieldId === 'gen-desc') {
                updateResult({desc: patch.text}, getFieldTargetProductIds('description'));
            } else if (fieldId.startsWith('gen-b-')) {
                const index = parseInt(fieldId.replace('gen-b-', ''), 10);
                updateBullet(
                    index,
                    {text: patch.text},
                    getFieldTargetProductIds('bullet', {bulletIndex: index}),
                );
            } else if (fieldId.startsWith('gen-faq-')) {
                // FAQ answer text — stored in faqs[index].answer.
                const index = parseInt(fieldId.replace('gen-faq-', ''), 10);
                updateFaq(
                    index,
                    {answer: patch.text},
                    getFieldTargetProductIds('faq', {faqIndex: index}),
                );
            }
        }
        if (patch.question !== undefined && fieldId.startsWith('gen-faq-')) {
            // FAQ question edited separately from the answer text.
            const index = parseInt(fieldId.replace('gen-faq-', ''), 10);
            updateFaq(
                index,
                {question: patch.question},
                getFieldTargetProductIds('faq', {faqIndex: index}),
            );
        }
        setResultFieldMeta((prev) => ({
            ...prev,
            [fieldId]: {...(prev[fieldId] || {}), ...patch},
        }));
    };

    const regenerateResultField = async (field) => {
        if (!field || !currentProduct) return '';

        const enabledState = {
            title: field.type === 'title',
            desc: field.type === 'description',
            bullets: field.type === 'bullet',
            faqs: field.type === 'faq',
        };

        let requestRows = [];
        const bulletIndex =
            field.type === 'bullet' ? parseInt(field.id.replace('gen-b-', ''), 10) : -1;
        const faqIndex =
            field.type === 'faq' ? parseInt(field.id.replace('gen-faq-', ''), 10) : -1;

        if (field.type === 'bullet') {
            const sourceRow = rows[bulletIndex];
            requestRows = [
                {
                    id: sourceRow?.id || field.id,
                    field: sourceRow?.field || field.label || `Bullet ${bulletIndex + 1}`,
                    kw: sourceRow?.kw || '',
                },
            ];
        } else if (field.type === 'faq') {
            // FAQ regen uses the matching features row as the topic so
            // the AI rewrites a Q&A on the same subject. If no row maps
            // to this index, fall back to the field's label + question
            // as a soft topic hint.
            const sourceRow = rows[faqIndex];
            requestRows = [
                {
                    id: sourceRow?.id || field.id,
                    field: sourceRow?.field || field.label || `FAQ ${faqIndex + 1}`,
                    kw: sourceRow?.kw || field.question || '',
                },
            ];
        }

        const fieldDependencyMeta =
            field.type === 'title'
                ? buildFieldDependencyMetaFromTexts(
                      [topic],
                      vars,
                      currentProduct,
                      managedProductVarNames,
                  )
                : field.type === 'description'
                  ? buildFieldDependencyMetaFromTexts(
                        [topic, descriptionContext],
                        vars,
                        currentProduct,
                        managedProductVarNames,
                    )
                  : buildBulletFieldDependencyMeta(
                        requestRows[0] || {
                            field:
                                field.label || `Bullet ${bulletIndex + 1}`,
                            kw: '',
                        },
                        vars,
                        currentProduct,
                        managedProductVarNames,
                    );

        const targetProductIds =
            field.type === 'title'
                ? getFieldTargetProductIds('title')
                : field.type === 'description'
                  ? getFieldTargetProductIds('description')
                  : field.type === 'faq'
                    ? getFieldTargetProductIds('faq', {faqIndex})
                    : getFieldTargetProductIds('bullet', {bulletIndex});

        // When regenerating a single FAQ, scan the other FAQs on this
        // product, detect which opening patterns are already in use,
        // and lock the regen to the least-used pattern. No sibling
        // content is shipped to the AI — just a single pattern id.
        const siblingAnswers =
            field.type === 'faq'
                ? (currentResult?.faqs || [])
                      .map((faq, index) =>
                          index === faqIndex
                              ? null
                              : faq?.answer || faq?.text || '',
                      )
                      .filter((answer) => typeof answer === 'string' && answer.trim())
                : [];
        const openingPattern =
            field.type === 'faq' ? pickNextFaqOpening(siblingAnswers) : null;

        const nextResult = await generateAiContent({
            apiKey,
            topic,
            rows:
                field.type === 'bullet' || field.type === 'faq' ? requestRows : [],
            vars,
            tone,
            usecase,
            audience,
            enabled: enabledState,
            profileSettings: activeProfile?.settings,
            descriptionContext: field.type === 'description' ? descriptionContext : '',
            referenceImages: activeReferenceImages,
            brandContext,
            openingPattern,
        });

        setResultsByProductId((currentResults) => ({
            ...currentResults,
            ...Object.fromEntries(
                targetProductIds.map((productId) => {
                    const storedResult = currentResults[productId] || {};
                    const storedHighlights = storedResult.generationHighlights || {
                        title: [],
                        description: [],
                        bullets: [],
                    };
                    const storedFieldSubgroups = storedResult.fieldSubgroups || {
                        title: null,
                        description: null,
                        bullets: [],
                        faqs: [],
                    };
                    const targetProduct = selectedProductsById.get(productId) || currentProduct;

                    if (field.type === 'title') {
                        return [
                            productId,
                            {
                                ...storedResult,
                                title: nextResult.title || '',
                                generationHighlights: {
                                    ...storedHighlights,
                                    title: nextResult.generationHighlights?.title || [],
                                },
                                fieldSubgroups: {
                                    ...storedFieldSubgroups,
                                    title: buildFieldSubgroupMeta(
                                        getUniqueVarNamesForField('title'),
                                        vars,
                                        targetProduct,
                                        managedProductVarNames,
                                    ),
                                },
                                fieldDependencies: {
                                    ...(storedResult.fieldDependencies || {}),
                                    title: cloneFieldDependencyMeta(fieldDependencyMeta),
                                },
                            },
                        ];
                    }

                    if (field.type === 'description') {
                        return [
                            productId,
                            {
                                ...storedResult,
                                desc: nextResult.desc || '',
                                generationHighlights: {
                                    ...storedHighlights,
                                    description:
                                        nextResult.generationHighlights?.description || [],
                                },
                                fieldSubgroups: {
                                    ...storedFieldSubgroups,
                                    description: buildFieldSubgroupMeta(
                                        getUniqueVarNamesForField('description'),
                                        vars,
                                        targetProduct,
                                        managedProductVarNames,
                                    ),
                                },
                                fieldDependencies: {
                                    ...(storedResult.fieldDependencies || {}),
                                    description: cloneFieldDependencyMeta(fieldDependencyMeta),
                                },
                            },
                        ];
                    }

                    if (field.type === 'faq') {
                        const faqs = [...(storedResult.faqs || [])];
                        const nextFaq = nextResult.faqs?.[0] || null;
                        if (nextFaq) {
                            faqs[faqIndex] = {
                                ...(faqs[faqIndex] || {}),
                                ...nextFaq,
                            };
                        }
                        const faqHighlights = [...(storedHighlights.faqs || [])];
                        faqHighlights[faqIndex] =
                            nextResult.generationHighlights?.faqs?.[0] || [];
                        const faqFieldSubgroups = [...(storedFieldSubgroups.faqs || [])];
                        faqFieldSubgroups[faqIndex] = buildFieldSubgroupMeta(
                            getUniqueVarNamesForField('faq', {faqIndex}),
                            vars,
                            targetProduct,
                            managedProductVarNames,
                        );
                        return [
                            productId,
                            {
                                ...storedResult,
                                faqs,
                                generationHighlights: {
                                    ...storedHighlights,
                                    faqs: faqHighlights,
                                },
                                fieldSubgroups: {
                                    ...storedFieldSubgroups,
                                    faqs: faqFieldSubgroups,
                                },
                                fieldDependencies: {
                                    ...(storedResult.fieldDependencies || {}),
                                    faqs: (() => {
                                        const faqFieldDependencies = [
                                            ...((storedResult.fieldDependencies?.faqs || []).map(
                                                (meta) => cloneFieldDependencyMeta(meta),
                                            )),
                                        ];
                                        faqFieldDependencies[faqIndex] =
                                            cloneFieldDependencyMeta(fieldDependencyMeta);
                                        return faqFieldDependencies;
                                    })(),
                                },
                            },
                        ];
                    }

                    const bullets = [...(storedResult.bullets || [])];
                    const nextBullet = nextResult.bullets?.[0] || null;
                    if (nextBullet) {
                        bullets[bulletIndex] = {
                            ...(bullets[bulletIndex] || {}),
                            ...nextBullet,
                        };
                    }

                    const bulletHighlights = [...(storedHighlights.bullets || [])];
                    bulletHighlights[bulletIndex] =
                        nextResult.generationHighlights?.bullets?.[0] || [];
                    const bulletFieldSubgroups = [...(storedFieldSubgroups.bullets || [])];
                    bulletFieldSubgroups[bulletIndex] = buildFieldSubgroupMeta(
                        getUniqueVarNamesForField('bullet', {bulletIndex}),
                        vars,
                        targetProduct,
                        managedProductVarNames,
                    );

                    return [
                        productId,
                        {
                            ...storedResult,
                            bullets,
                            generationHighlights: {
                                ...storedHighlights,
                                bullets: bulletHighlights,
                            },
                            fieldSubgroups: {
                                ...storedFieldSubgroups,
                                bullets: bulletFieldSubgroups,
                            },
                            fieldDependencies: {
                                ...(storedResult.fieldDependencies || {}),
                                bullets: (() => {
                                    const bulletFieldDependencies = [
                                        ...((storedResult.fieldDependencies?.bullets || []).map(
                                            (meta) => cloneFieldDependencyMeta(meta),
                                        )),
                                    ];
                                    bulletFieldDependencies[bulletIndex] =
                                        cloneFieldDependencyMeta(fieldDependencyMeta);
                                    return bulletFieldDependencies;
                                })(),
                            },
                        },
                    ];
                }),
            ),
        }));

        if (field.type === 'title') return nextResult.title || '';
        if (field.type === 'description') return nextResult.desc || '';
        if (field.type === 'faq') return nextResult.faqs?.[0]?.answer || '';
        return nextResult.bullets?.[0]?.text || '';
    };

    const addChatMessage = (fieldId, message) => {
        setChatHistories((prev) => ({
            ...prev,
            [fieldId]: [...(prev[fieldId] || []), message],
        }));
    };

    // Build a virtual field object for a given field ID
    const buildVirtualField = (fieldId) => {
        const meta = resultFieldMeta[fieldId] || {};
        if (fieldId === 'gen-title') {
            return {
                id: fieldId,
                type: 'title',
                label: 'Title',
                text: currentResult?.title || '',
                status: meta.status || 'draft',
                history: meta.history || [],
                searchTerms: meta.searchTerms || [],
            };
        }
        if (fieldId === 'gen-desc') {
            return {
                id: fieldId,
                type: 'description',
                label: 'Description',
                text: currentResult?.desc || '',
                status: meta.status || 'draft',
                history: meta.history || [],
                searchTerms: meta.searchTerms || [],
            };
        }
        if (fieldId?.startsWith('gen-b-')) {
            const index = parseInt(fieldId.replace('gen-b-', ''), 10);
            const bullet = currentResult?.bullets?.[index];
            return {
                id: fieldId,
                type: 'bullet',
                label: bullet?.field || 'Bullet',
                text: bullet?.text || '',
                status: meta.status || 'draft',
                history: meta.history || [],
                searchTerms: meta.searchTerms || [],
            };
        }
        if (fieldId?.startsWith('gen-faq-')) {
            const index = parseInt(fieldId.replace('gen-faq-', ''), 10);
            const faq = currentResult?.faqs?.[index];
            return {
                id: fieldId,
                type: 'faq',
                label: `FAQ ${index + 1}`,
                question: faq?.question || '',
                text: faq?.answer || '',
                status: meta.status || 'draft',
                history: meta.history || [],
                searchTerms: meta.searchTerms || [],
            };
        }
        return null;
    };

    // Virtual listing for ChatPanel context
    const chatListing = currentProduct
        ? {
              id: currentProduct.id,
              name: currentProduct.name || currentProduct.sku,
              sku: currentProduct.sku || '',
              category: currentProduct.category || '',
              fields: [
                  ...(currentResult?.title
                      ? [
                            {
                                id: 'gen-title',
                                type: 'title',
                                label: 'Title',
                                text: currentResult.title,
                            },
                        ]
                      : []),
                  ...(currentResult?.desc
                      ? [
                            {
                                id: 'gen-desc',
                                type: 'description',
                                label: 'Description',
                                text: currentResult.desc,
                            },
                        ]
                      : []),
                  ...(currentResult?.bullets || []).map((b, i) => ({
                      id: `gen-b-${i}`,
                      type: 'bullet',
                      label: b.field,
                      text: b.text,
                  })),
                  ...(currentResult?.faqs || []).map((faq, i) => ({
                      id: `gen-faq-${i}`,
                      type: 'faq',
                      label: `FAQ ${i + 1}`,
                      question: faq.question || '',
                      text: faq.answer || '',
                  })),
              ],
          }
        : null;

    const activeChatField = chatFieldId ? buildVirtualField(chatFieldId) : null;

    const resolveResultText = (text) =>
        text && text.includes('{{') ? resolveVars(text, vars) : text || '';

    const exportResults = (format) => {
        const productsWithResults = selectedProducts.filter((product) => resultsByProductId[product.id]);
        const counts = productsWithResults.reduce(
            (acc, product) => {
                const output = countGeneratedOutput(resultsByProductId[product.id]);
                return {
                    products: acc.products + 1,
                    titles: acc.titles + output.titles,
                    descriptions: acc.descriptions + output.descriptions,
                    bullets: acc.bullets + output.bullets,
                    faqs: acc.faqs + output.faqs,
                };
            },
            {products: 0, titles: 0, descriptions: 0, bullets: 0, faqs: 0},
        );

        exportGeneratedResults(selectedProducts, resultsByProductId, resolveResultText, format);
        trackUsageEvent('export.generator', {format, ...counts});
    };

    const brandContext = useMemo(() => buildBrandContext(brandProfile), [brandProfile]);
    const hasAnyResults = selectedProducts.some((p) => resultsByProductId[p.id]);

    const openProfileDialog = (profile = null) => {
        const color = profile?.color || activeProfile?.color || defaultProfile.color;
        const useCurrentGeneratorSettings = !profile || profile.id === activeProfileId;

        setProfileDialogState({
            open: true,
            profile: {
                ...(profile || {}),
                color,
                settings: {
                    ...buildProfileSettings(profile?.settings, {useCurrentGeneratorSettings}),
                    color,
                },
            },
        });
    };

    const closeProfileDialog = () =>
        setProfileDialogState({
            open: false,
            profile: null,
        });

    const persistActiveProfile = async () => {
        if (!activeProfile) return;

        setProfileActionPending(true);
        try {
            const savedProfile = await saveProfile({
                ...activeProfile,
                settings: {
                    ...buildProfileSettings(activeProfile.settings, {
                        useCurrentGeneratorSettings: true,
                    }),
                    color: activeProfile.color,
                },
            });
            applyProfile(savedProfile);
        } catch (profileError) {
            setUiError(profileError.message || 'Could not save the active profile.');
        } finally {
            setProfileActionPending(false);
        }
    };

    const handleProfileDialogSave = async (draftProfile) => {
        if (!draftProfile?.name?.trim()) return;

        setProfileActionPending(true);
        try {
            const useCurrentGeneratorSettings =
                !draftProfile.id || draftProfile.id === activeProfileId;
            const color =
                draftProfile.color || draftProfile.settings?.color || defaultProfile.color;
            const savedProfile = await saveProfile({
                ...draftProfile,
                color,
                settings: {
                    ...buildProfileSettings(draftProfile.settings, {
                        useCurrentGeneratorSettings,
                    }),
                    color,
                },
            });

            if (!draftProfile.id || draftProfile.id === activeProfileId) {
                applyProfile(savedProfile);
            }

            closeProfileDialog();
        } catch (profileError) {
            setUiError(profileError.message || 'Could not save the profile.');
        } finally {
            setProfileActionPending(false);
        }
    };

    const handleDeleteProfile = async (profile, event) => {
        event.stopPropagation();
        if (!profile || profiles.length <= 1) return;

        setProfileActionPending(true);
        try {
            const remainingProfiles = profiles.filter((candidate) => candidate.id !== profile.id);
            await deleteProfile(profile);

            if (activeProfileId === profile.id) {
                if (remainingProfiles.length > 0) {
                    applyProfile(remainingProfiles[0]);
                } else {
                    setActiveProfileId('');
                }
            }
        } catch (profileError) {
            setUiError(profileError.message || 'Could not delete the profile.');
        } finally {
            setProfileActionPending(false);
        }
    };

    useEffect(() => {
        if (!profiles.length) return;
        if (!profiles.some((profile) => profile.id === activeProfileId)) {
            applyProfile(profiles[0]);
        }
    }, [activeProfileId, applyProfile, profiles]);

    // Reset editor-specific state when the active product changes.
    // Note: descriptionContext is intentionally NOT reset here — it's a
    // single global value that persists across product navigation.
    useEffect(() => {
        if (!currentProduct) return;
        const nextKey = `${collectionId}:${productSource}:${currentProduct.id}`;
        if (appliedProductRef.current === nextKey) return;
        appliedProductRef.current = nextKey;
        const nextDraft = applySharedSubgroupSelectionsToDraft(
            draftsByProductId[currentProduct.id] || buildGeneratorDraft(currentProduct),
            sharedSubgroupSelectionsRef.current,
        );

        setTopic(nextDraft.topic);
        setIncludeProductImage(Boolean(nextDraft.includeProductImage && currentProduct.imageUrl));
        setReferenceImages(cloneReferenceImages(nextDraft.referenceImages));
        setActiveWizard(null);
        setOpenBulletSubgroupRowId(null);
        setUiError('');
    }, [collectionId, buildGeneratorDraft, currentProduct, draftsByProductId, productSource]);

    useEffect(() => {
        if (!currentProduct) return;

        setDraftsByProductId((currentDrafts) => {
            const currentDraft = currentDrafts[currentProduct.id];
            if (
                currentDraft &&
                currentDraft.topic === topic &&
                currentDraft.titleUniqueVarNames === titleUniqueVarNames &&
                currentDraft.descriptionUniqueVarNames === descriptionUniqueVarNames &&
                currentDraft.includeProductImage === includeProductImage &&
                currentDraft.rows === rows &&
                currentDraft.referenceImages === referenceImages
            ) {
                return currentDrafts;
            }

            return {
                ...currentDrafts,
                [currentProduct.id]: {
                    topic,
                    titleUniqueVarNames,
                    rows,
                    descriptionUniqueVarNames,
                    includeProductImage,
                    referenceImages,
                },
            };
        });
    }, [
        currentProduct,
        descriptionUniqueVarNames,
        includeProductImage,
        referenceImages,
        rows,
        titleUniqueVarNames,
        topic,
    ]);

    const getReferenceImagesForProduct = useCallback((product, draft) => {
        const productImages =
            draft?.includeProductImage && product?.imageUrl
                ? [
                      {
                          id: `${product.id}-catalog-image`,
                          name: `${product.name || product.sku} catalog image`,
                          url: product.imageUrl,
                          source: 'catalog',
                      },
                  ]
                : [];

        return [...productImages, ...cloneReferenceImages(draft?.referenceImages)];
    }, []);

    const addRow = () => {
        // In FAQ mode the row list is driven by faqCount — keep them in
        // lock-step so "+ Add FAQ topic" and the stepper agree. The cap
        // matches normalizeFaqCount's upper bound; clicking + at the
        // ceiling is a no-op.
        if (enabled.faqs) {
            const nextCount = normalizeFaqCount(faqCount + 1);
            if (nextCount === faqCount) return;
            setFaqCount(nextCount);
        }
        setBulletUniqueVarNamesByIndex((currentNamesByIndex) => [
            ...normalizeUniqueVarNamesByIndex(currentNamesByIndex),
            [],
        ]);
        setRowsWithDraft((currentRows) => [
            ...currentRows,
            {id: uid(), field: '', kw: '', uniqueVarNames: []},
        ]);
    };

    // Drives row count from the FAQ stepper. Adds empty rows when
    // increasing, trims from the bottom when decreasing. Only mutates
    // rows while FAQs are the active section — flipping the count while
    // bullets are active just stores the preference.
    const updateFaqCount = (nextCount) => {
        const clamped = normalizeFaqCount(nextCount);
        setFaqCount(clamped);
        if (!enabled.faqs) return;
        setRowsWithDraft((current) => syncRowsToFaqCount(current, clamped));
        setBulletUniqueVarNamesByIndex((currentNames) =>
            syncUniqueVarNamesToFaqCount(currentNames, clamped),
        );
    };

    // When FAQs toggles on, sync the rows array to faqCount so the user
    // immediately sees the expected number of empty topic slots —
    // otherwise they have to click +/− to grow from 0 rows. The ref
    // tracks the previous state so the sync fires only on the
    // off→on transition (not every render while FAQs is active).
    const prevFaqsEnabledRef = useRef(enabled.faqs);
    useEffect(() => {
        if (enabled.faqs && !prevFaqsEnabledRef.current) {
            updateFaqCount(activeProfile?.settings?.faqs?.faqCount ?? faqCount);
        }
        prevFaqsEnabledRef.current = enabled.faqs;
        // updateFaqCount/faqCount intentionally omitted: we only want to
        // react to the toggle transition, not re-sync on every count or
        // function-identity change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled.faqs]);

    // AI bulk-fill all topic rows. Wires PromptWizard's generateAiAngles
    // in faqs mode and produces exactly faqCount rows. AI suggestions
    // fill from the top; if AI returns fewer than expected, the trailing
    // slots fall back to whatever was there (or empty).
    const suggestFaqTopics = async () => {
        if (suggestingFaqTopics) return;
        setSuggestingFaqTopics(true);
        setUiError('');
        try {
            const suggestions = await generateAiAngles({
                vars,
                brandProfile,
                product: currentProduct,
                count: faqCount,
                wizardMode: 'faqs',
            });
            if (!Array.isArray(suggestions) || !suggestions.length) {
                throw new Error('AI returned no topic suggestions.');
            }
            // AI often returns multiple {{var}} tokens that resolve to
            // the same value for this variant — collapse them so each
            // FAQ topic carries one token per distinct value.
            const dedupedSuggestions = suggestions.map((suggestion) => ({
                ...suggestion,
                kw: dedupeFormulaTokensByResolvedValue(suggestion?.kw || '', vars),
            }));
            setRowsWithDraft((current) =>
                Array.from({length: faqCount}, (_unused, index) => {
                    const suggestion = dedupedSuggestions[index];
                    if (suggestion) {
                        return {
                            id: current[index]?.id || uid(),
                            field: suggestion.field || '',
                            kw: suggestion.kw || '',
                            uniqueVarNames: current[index]?.uniqueVarNames || [],
                        };
                    }
                    return (
                        current[index] || {
                            id: uid(),
                            field: '',
                            kw: '',
                            uniqueVarNames: [],
                        }
                    );
                }),
            );
            setBulletUniqueVarNamesByIndex((currentNames) => {
                const normalized = normalizeUniqueVarNamesByIndex(currentNames);
                return Array.from({length: faqCount}, (_unused, index) =>
                    normalized[index] || [],
                );
            });
        } catch (error) {
            setUiError(error.message || 'Could not suggest FAQ topics.');
        } finally {
            setSuggestingFaqTopics(false);
        }
    };

    const removeRow = (id) => {
        const rowIndex = rows.findIndex((row) => row.id === id);
        // Match the stepper's lower bound — can't drop faqCount below
        // 1. Clicking × on the last remaining FAQ row is a no-op so the
        // user always has at least one topic slot to work with.
        if (enabled.faqs) {
            const nextCount = normalizeFaqCount(faqCount - 1);
            if (nextCount === faqCount) return;
            setFaqCount(nextCount);
        }
        setRowsWithDraft((currentRows) => currentRows.filter((row) => row.id !== id));
        if (rowIndex >= 0) {
            setBulletUniqueVarNamesByIndex((currentNamesByIndex) =>
                normalizeUniqueVarNamesByIndex(currentNamesByIndex).filter(
                    (_, index) => index !== rowIndex,
                ),
            );
        }
        if (activeWizard === id) setActiveWizard(null);
        if (openBulletSubgroupRowId === id) setOpenBulletSubgroupRowId(null);
    };

    useEffect(() => {
        if (!openBulletSubgroupRowId) return;

        const openRow = rows.find((row) => row.id === openBulletSubgroupRowId);
        if (!openRow) {
            setOpenBulletSubgroupRowId(null);
            return;
        }

        const rowSelectableVarNames = extractSelectableFormulaVarNames([
            openRow.field || '',
            openRow.kw || '',
        ]);
        if (!rowSelectableVarNames.length && !(openRow.uniqueVarNames || []).length) {
            setOpenBulletSubgroupRowId(null);
        }
    }, [openBulletSubgroupRowId, rows]);

    const updateRow = (id, key, value) =>
        setRowsWithDraft((currentRows) => {
            const rowIndex = currentRows.findIndex((row) => row.id === id);

            return currentRows.map((row, index) => {
                if (row.id !== id) return row;

                const nextRow = normalizeGenerationRow({...row, [key]: value});
                if (key !== 'field' && key !== 'kw') return nextRow;

                const selectableVarNames = extractSelectableFormulaVarNames([
                    nextRow.field || '',
                    nextRow.kw || '',
                ]);
                const filteredUniqueVarNames = normalizeUniqueVarNames(
                    nextRow.uniqueVarNames.filter((name) => selectableVarNames.includes(name)),
                );

                if (rowIndex >= 0) {
                    setBulletUniqueVarNamesByIndex((currentNamesByIndex) => {
                        const nextNamesByIndex = normalizeUniqueVarNamesByIndex(
                            currentNamesByIndex,
                        );
                        nextNamesByIndex[index] = filteredUniqueVarNames;
                        return nextNamesByIndex;
                    });
                }

                return {
                    ...nextRow,
                    uniqueVarNames: filteredUniqueVarNames,
                };
            });
        });

    const toggleRowUniqueVarName = useCallback(
        (rowId, varName) => {
            const rowIndex = rows.findIndex((row) => row.id === rowId);
            if (rowIndex < 0) return;

            const currentUniqueVarNames = normalizeUniqueVarNames(
                bulletUniqueVarNamesByIndex[rowIndex] || rows[rowIndex]?.uniqueVarNames,
            );
            const nextUniqueVarNames = normalizeUniqueVarNames(
                toggleUniqueVarName(currentUniqueVarNames, varName),
            );

            setBulletUniqueVarNamesByIndex((currentNamesByIndex) => {
                const nextNamesByIndex = normalizeUniqueVarNamesByIndex(currentNamesByIndex);
                nextNamesByIndex[rowIndex] = nextUniqueVarNames;
                return nextNamesByIndex;
            });
            setRowsWithDraft((currentRows) =>
                currentRows.map((row, index) =>
                    index === rowIndex ? {...row, uniqueVarNames: nextUniqueVarNames} : row,
                ),
            );
        },
        [bulletUniqueVarNamesByIndex, rows, setRowsWithDraft],
    );

    const toggleEnabled = (key) =>
        setEnabled((current) => {
            const next = {...current, [key]: !current[key]};
            // Bullets and FAQs are mutually exclusive — they share the
            // features rows for generation, so only one can be active at
            // a time. Turning one on automatically turns the other off.
            if (key === 'bullets' && next.bullets) next.faqs = false;
            if (key === 'faqs' && next.faqs) next.bullets = false;
            if (!next.title && !next.desc && !next.bullets && !next.faqs) return current;
            return next;
        });

    const copy = async (key, text) => {
        try {
            await copyText(text);
            setCopied((current) => ({...current, [key]: true}));
            setTimeout(() => {
                setCopied((current) => ({...current, [key]: false}));
            }, 1400);
        } catch (copyError) {
            setUiError(copyError.message || 'Could not copy the generated text.');
        }
    };

    const copyAll = () => {
        if (!currentResult) return;

        const lines = [
            enabled.title && currentResult.title ? `Title:\n${resolveResultText(currentResult.title)}` : '',
            enabled.desc && currentResult.desc ? `\nDescription:\n${resolveResultText(currentResult.desc)}` : '',
            enabled.bullets && currentResult.bullets?.length
                ? `\nBullets:\n${currentResult.bullets.map((bullet) => `• ${resolveResultText(bullet.text)}`).join('\n')}`
                : '',
            enabled.faqs && currentResult.faqs?.length
                ? `\nFAQs:\n${currentResult.faqs
                      .map(
                          (faq) =>
                              `Q: ${resolveResultText(faq.question)}\nA: ${resolveResultText(faq.answer)}`,
                      )
                      .join('\n\n')}`
                : '',
        ]
            .filter(Boolean)
            .join('\n');

        copy(`${currentProduct?.id || 'product'}-all`, lines);
    };

    // When switching edited field while chat is open, move chat to the new field
    useEffect(() => {
        if (editingField && chatFieldId) {
            const expectedChatId = `gen-${editingField}`;
            if (chatFieldId !== expectedChatId) {
                setChatFieldId(expectedChatId);
            }
        }
    }, [editingField, chatFieldId]);

    const generate = useCallback(async () => {
        if (!selectedProducts.length) {
            setUiError('Select a SKU to generate content.');
            return;
        }

        // Close editor before regenerating
        if (editingField) {
            setEditingField(null);
        }

        setUiError('');
        clearAiError();
        setActiveWizard(null);
        setResultsByProductId((currentResults) => ({
            ...currentResults,
            ...Object.fromEntries(selectedProducts.map((product) => [product.id, null])),
        }));

        try {
            const generationTasks = productGenerationGroups.flatMap((group) => {
                const draft = getDraftForProduct(group.templateProduct);

                return buildScopedGenerationTasks({
                    products: group.products,
                    baseVars: vars,
                    managedProductVarNames,
                    preferredProductId: group.templateProduct?.id || '',
                    topic: draft.topic,
                    descriptionContext: draft.descriptionContext,
                    rows: draft.rows,
                    enabled,
                    titleUniqueVarNames: draft.titleUniqueVarNames,
                    descriptionUniqueVarNames: draft.descriptionUniqueVarNames,
                    bulletUniqueVarNamesByIndex: draft.rows.map(
                        (row) => normalizeUniqueVarNames(row.uniqueVarNames),
                    ),
                }).map((task) => ({...task, group}));
            });
            const pendingResults = {};
            const generatedCounts = {
                tasks: generationTasks.length,
                products: 0,
                titles: 0,
                descriptions: 0,
                bullets: 0,
                fullTasks: 0,
                titleTasks: 0,
                descriptionTasks: 0,
                bulletTasks: 0,
                faqTasks: 0,
                faqs: 0,
            };
            const commitPendingResults = () => {
                setResultsByProductId((currentResults) => ({
                    ...currentResults,
                    ...Object.fromEntries(
                        Object.entries(pendingResults).map(([productId, result]) => [
                            productId,
                            cloneGeneratedResult(result),
                        ]),
                    ),
                }));
            };
            const ensurePendingResult = (productId, draft) => {
                if (!pendingResults[productId]) {
                    pendingResults[productId] = buildEmptyGeneratedResult(draft, enabled);
                }

                return pendingResults[productId];
            };

            setBatchGenerationState({
                stepIndex: 0,
                totalSteps: generationTasks.length,
                totalGroups: productGenerationGroups.length,
                totalSkus: selectedProducts.length,
            });

            for (const [taskIndex, task] of generationTasks.entries()) {
                const templateProduct =
                    selectedProductsById.get(task.templateProductId) || task.group.templateProduct;
                const draft = getDraftForProduct(templateProduct);
                const taskVars = reconcileProductVars(
                    vars,
                    templateProduct?.vars || [],
                    managedProductVarNames,
                );

                setBatchGenerationState({
                    stepIndex: taskIndex + 1,
                    totalSteps: generationTasks.length,
                    totalGroups: productGenerationGroups.length,
                    totalSkus: selectedProducts.length,
                    templateSku: templateProduct?.sku || templateProduct?.id || '',
                    scopeLabel:
                        task.type === 'full'
                            ? 'Full content'
                            : task.type === 'description'
                              ? 'Description'
                              : task.type === 'title'
                                ? 'Title'
                                : task.type === 'faq'
                                  ? `FAQ ${task.rowIndex + 1}`
                                  : `Bullet ${task.rowIndex + 1}`,
                });

                if (task.type === 'full') {
                    const nextResult = await generateAiContent({
                        apiKey,
                        topic: draft.topic,
                        rows: draft.rows,
                        vars: taskVars,
                        tone,
                        usecase,
                        audience,
                        enabled,
                        profileSettings: activeProfile?.settings,
                        descriptionContext: draft.descriptionContext,
                        referenceImages: getReferenceImagesForProduct(templateProduct, draft),
                        brandContext,
                    });
                    const nextFieldDependencies = buildGeneratedFieldDependencies({
                        draft,
                        baseVars: vars,
                        templateProduct,
                        managedProductVarNames,
                        enabledState: enabled,
                    });

                    task.productIds.forEach((productId) => {
                        const product = selectedProductsById.get(productId) || templateProduct;
                        const targetDraft = getDraftForProduct(product);
                        pendingResults[productId] = {
                            ...buildEmptyGeneratedResult(targetDraft, enabled),
                            ...cloneGeneratedResult(nextResult),
                            fieldDependencies: {
                                title: cloneFieldDependencyMeta(nextFieldDependencies.title),
                                description: cloneFieldDependencyMeta(
                                    nextFieldDependencies.description,
                                ),
                                bullets: (nextFieldDependencies.bullets || []).map((meta) =>
                                    cloneFieldDependencyMeta(meta),
                                ),
                                faqs: (nextFieldDependencies.faqs || []).map((meta) =>
                                    cloneFieldDependencyMeta(meta),
                                ),
                            },
                        };
                    });
                    const output = countGeneratedOutput(nextResult);
                    generatedCounts.fullTasks += 1;
                    generatedCounts.products += task.productIds.length;
                    generatedCounts.titles += output.titles * task.productIds.length;
                    generatedCounts.descriptions += output.descriptions * task.productIds.length;
                    generatedCounts.bullets += output.bullets * task.productIds.length;
                    generatedCounts.faqs += output.faqs * task.productIds.length;
                    commitPendingResults();
                    continue;
                }

                const enabledState = {
                    title: task.type === 'title',
                    desc: task.type === 'description',
                    bullets: task.type === 'bullet',
                    faqs: task.type === 'faq',
                };
                const taskRow =
                    task.type === 'bullet' || task.type === 'faq'
                        ? draft.rows?.[task.rowIndex] || {
                              id: task.rowId || `row-${task.rowIndex}`,
                              field: '',
                              kw: '',
                          }
                        : null;
                const nextResult = await generateAiContent({
                    apiKey,
                    topic: draft.topic,
                    rows: taskRow ? [taskRow] : [],
                    vars: taskVars,
                    tone,
                    usecase,
                    audience,
                    enabled: enabledState,
                    profileSettings: activeProfile?.settings,
                    descriptionContext: task.type === 'description' ? draft.descriptionContext : '',
                    referenceImages: getReferenceImagesForProduct(templateProduct, draft),
                    brandContext,
                });

                task.productIds.forEach((productId) => {
                    const product = selectedProductsById.get(productId) || templateProduct;
                    const targetDraft = getDraftForProduct(product);
                    const storedResult = ensurePendingResult(productId, targetDraft);

                    if (task.type === 'title') {
                        storedResult.title = nextResult.title || '';
                        storedResult.generationHighlights.title = [
                            ...(nextResult.generationHighlights?.title || []),
                        ];
                        storedResult.fieldSubgroups = {
                            ...(storedResult.fieldSubgroups || {}),
                            title: buildFieldSubgroupMeta(
                                task.uniqueVarNames,
                                vars,
                                product,
                                managedProductVarNames,
                            ),
                        };
                        storedResult.fieldDependencies = {
                            ...(storedResult.fieldDependencies || {}),
                            title: buildFieldDependencyMetaFromTexts(
                                [draft.topic],
                                vars,
                                templateProduct,
                                managedProductVarNames,
                            ),
                        };
                        return;
                    }

                    if (task.type === 'description') {
                        storedResult.desc = nextResult.desc || '';
                        storedResult.generationHighlights.description = [
                            ...(nextResult.generationHighlights?.description || []),
                        ];
                        storedResult.fieldSubgroups = {
                            ...(storedResult.fieldSubgroups || {}),
                            description: buildFieldSubgroupMeta(
                                task.uniqueVarNames,
                                vars,
                                product,
                                managedProductVarNames,
                            ),
                        };
                        storedResult.fieldDependencies = {
                            ...(storedResult.fieldDependencies || {}),
                            description: buildFieldDependencyMetaFromTexts(
                                [draft.topic, draft.descriptionContext],
                                vars,
                                templateProduct,
                                managedProductVarNames,
                            ),
                        };
                        return;
                    }

                    if (task.type === 'faq') {
                        const faqs = [...(storedResult.faqs || [])];
                        const nextFaq = nextResult.faqs?.[0] || null;
                        faqs[task.rowIndex] = nextFaq
                            ? {
                                  ...(faqs[task.rowIndex] || {}),
                                  ...nextFaq,
                              }
                            : {
                                  ...(faqs[task.rowIndex] || {}),
                                  question: faqs[task.rowIndex]?.question || '',
                                  answer: faqs[task.rowIndex]?.answer || '',
                              };
                        storedResult.faqs = faqs;

                        const faqHighlights = [
                            ...(storedResult.generationHighlights?.faqs || []),
                        ];
                        faqHighlights[task.rowIndex] =
                            nextResult.generationHighlights?.faqs?.[0] || [];
                        storedResult.generationHighlights = {
                            ...storedResult.generationHighlights,
                            faqs: faqHighlights,
                        };
                        const faqFieldSubgroups = [
                            ...(storedResult.fieldSubgroups?.faqs || []),
                        ];
                        faqFieldSubgroups[task.rowIndex] = buildFieldSubgroupMeta(
                            task.uniqueVarNames,
                            vars,
                            product,
                            managedProductVarNames,
                        );
                        storedResult.fieldSubgroups = {
                            ...(storedResult.fieldSubgroups || {}),
                            faqs: faqFieldSubgroups,
                        };
                        const faqFieldDependencies = [
                            ...((storedResult.fieldDependencies?.faqs || []).map((meta) =>
                                cloneFieldDependencyMeta(meta),
                            )),
                        ];
                        faqFieldDependencies[task.rowIndex] = buildBulletFieldDependencyMeta(
                            taskRow,
                            vars,
                            templateProduct,
                            managedProductVarNames,
                        );
                        storedResult.fieldDependencies = {
                            ...(storedResult.fieldDependencies || {}),
                            faqs: faqFieldDependencies,
                        };
                        return;
                    }

                    const bullets = [...(storedResult.bullets || [])];
                    const nextBullet = nextResult.bullets?.[0] || null;
                    bullets[task.rowIndex] = nextBullet
                        ? {
                              ...(bullets[task.rowIndex] || {}),
                              ...nextBullet,
                          }
                        : {
                              ...(bullets[task.rowIndex] || {}),
                              field:
                                  bullets[task.rowIndex]?.field ||
                                  taskRow?.field ||
                                  `Bullet ${task.rowIndex + 1}`,
                              text: bullets[task.rowIndex]?.text || '',
                          };
                    storedResult.bullets = bullets;

                    const bulletHighlights = [
                        ...(storedResult.generationHighlights?.bullets || []),
                    ];
                    bulletHighlights[task.rowIndex] =
                        nextResult.generationHighlights?.bullets?.[0] || [];
                    storedResult.generationHighlights = {
                        ...storedResult.generationHighlights,
                        bullets: bulletHighlights,
                    };
                    const bulletFieldSubgroups = [
                        ...(storedResult.fieldSubgroups?.bullets || []),
                    ];
                    bulletFieldSubgroups[task.rowIndex] = buildFieldSubgroupMeta(
                        task.uniqueVarNames,
                        vars,
                        product,
                        managedProductVarNames,
                    );
                    storedResult.fieldSubgroups = {
                        ...(storedResult.fieldSubgroups || {}),
                        bullets: bulletFieldSubgroups,
                    };
                    const bulletFieldDependencies = [
                        ...((storedResult.fieldDependencies?.bullets || []).map((meta) =>
                            cloneFieldDependencyMeta(meta),
                        )),
                    ];
                    bulletFieldDependencies[task.rowIndex] = buildBulletFieldDependencyMeta(
                        taskRow,
                        vars,
                        templateProduct,
                        managedProductVarNames,
                    );
                    storedResult.fieldDependencies = {
                        ...(storedResult.fieldDependencies || {}),
                        bullets: bulletFieldDependencies,
                    };
                });
                const output = countGeneratedOutput(nextResult);
                generatedCounts.products += task.productIds.length;
                if (task.type === 'title') {
                    generatedCounts.titleTasks += 1;
                    generatedCounts.titles += output.titles * task.productIds.length;
                } else if (task.type === 'description') {
                    generatedCounts.descriptionTasks += 1;
                    generatedCounts.descriptions += output.descriptions * task.productIds.length;
                } else if (task.type === 'bullet') {
                    generatedCounts.bulletTasks += 1;
                    generatedCounts.bullets += output.bullets * task.productIds.length;
                } else if (task.type === 'faq') {
                    generatedCounts.faqTasks += 1;
                    generatedCounts.faqs += output.faqs * task.productIds.length;
                }

                commitPendingResults();
            }
            trackUsageEvent('generation.completed', {
                ...generatedCounts,
                groups: productGenerationGroups.length,
                selectedProducts: selectedProducts.length,
                titleEnabled: enabled.title,
                descriptionEnabled: enabled.desc,
                bulletsEnabled: enabled.bullets,
                faqsEnabled: enabled.faqs,
            });
        } catch {
            trackUsageEvent('generation.failed', {
                selectedProducts: selectedProducts.length,
                groups: productGenerationGroups.length,
            });
            // Error state is owned by the hook.
        } finally {
            setBatchGenerationState(null);
        }
    }, [
        activeProfile?.settings,
        apiKey,
        audience,
        buildGeneratorDraft,
        clearAiError,
        currentProduct,
        descriptionContext,
        editingField,
        enabled,
        generateAiContent,
        getDraftForProduct,
        getReferenceImagesForProduct,
        productGenerationGroups,
        selectedProductsById,
        referenceImages,
        rows,
        selectedProducts,
        setResultsByProductId,
        tone,
        topic,
        usecase,
        vars,
        includeProductImage,
    ]);

    const generationError = uiError || aiError;
    const isFullGenerating = aiLoading || Boolean(batchGenerationState);
    const generationProgress = batchGenerationState?.totalSteps
        ? Math.max(
              0,
              Math.min(
                  100,
                  Math.round(
                      (batchGenerationState.stepIndex / batchGenerationState.totalSteps) * 100,
                  ),
              ),
          )
        : aiLoading
          ? 12
          : 0;
    const generationStatusLabel = batchGenerationState?.totalSkus > 1
        ? batchGenerationState?.totalSteps
            ? `Writing ${batchGenerationState.scopeLabel || 'content'} (${batchGenerationState.stepIndex}/${batchGenerationState.totalSteps}) for ${batchGenerationState.totalSkus} selected SKUs.`
            : `Writing content for ${batchGenerationState.totalSkus} selected SKUs across ${batchGenerationState.totalGroups} group${batchGenerationState.totalGroups === 1 ? '' : 's'}.`
        : `Writing content for ${currentProduct?.sku || 'the current SKU'}.`;
    const fieldCardMaxChars = {
        title:
            activeProfile?.settings.title?.maxChars ??
            DEFAULT_PROFILE_SECTION_SETTINGS.title.maxChars,
        description:
            activeProfile?.settings.description?.maxChars ??
            DEFAULT_PROFILE_SECTION_SETTINGS.description.maxChars,
        bullet:
            activeProfile?.settings.bullets?.maxChars ??
            DEFAULT_PROFILE_SECTION_SETTINGS.bullets.maxChars,
        faq:
            activeProfile?.settings.faqs?.maxAnswerChars ??
            DEFAULT_PROFILE_SECTION_SETTINGS.faqs.maxAnswerChars,
    };
    const actionBtnStyle = {
        fontSize: 10.5,
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: 7,
        cursor: 'pointer',
        fontFamily: 'inherit',
        lineHeight: 1.2,
        border: `1px solid ${C.border}`,
        background: C.surfaceRaised,
        color: C.textSoft,
    };
    const actionBtnPrimaryStyle = {
        ...actionBtnStyle,
        color: C.blue,
        background: C.blueL,
        border: `1px solid ${C.blue}33`,
    };
    const actionGroupStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    };
    const topicInputExpanded = focusedGeneratorInput === 'topic';
    const descriptionContextExpanded =
        focusedGeneratorInput === 'description' || showDescriptionWizard;

    return (
        <div
            style={{
                fontFamily: "'DM Sans',system-ui,sans-serif",
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                minWidth: 0,
                background: 'var(--cg-canvas)',
                color: C.ink,
                border: `1px solid ${C.border2}`,
                borderRadius: 16,
                overflow: 'visible',
                transition: 'background-color .2s ease, color .2s ease, border-color .2s ease',
                ...themeVars,
            }}>
            <div
                className="content-generator-shell"
                style={{
                    width: '100%',
                    alignItems: 'stretch',
                    borderRadius: 'inherit',
                    overflow: 'visible',
                    background: C.surface,
                    // Inline width overrides the CSS default on desktop so
                    // the user's drag-resized width takes effect. The
                    // mobile media query below uses !important to fall
                    // back to a single-column stack.
                    gridTemplateColumns: `${generatorRailWidth}px 6px minmax(0, 1fr)`,
                }}>
                <div
                    ref={generatorRailRef}
                    className="content-generator-rail"
                    style={{
                        borderRight: `1px solid ${C.border}`,
                        background: C.surface,
                        // Container queries below in the inline style
                        // block adapt the bullet/FAQ row layout based on
                        // the rail's actual rendered width — needed
                        // because drag-resize is no longer correlated
                        // with viewport width.
                        containerType: 'inline-size',
                        display: 'flex',
                        flexDirection: 'column',
                        // The rail follows normal page scroll; only the
                        // generate footer is sticky to the workspace scroll
                        // container's bottom.
                        position: 'relative',
                        alignSelf: 'stretch',
                        height: 'auto',
                        maxHeight: 'none',
                        minHeight: 0,
                        overflow: 'visible',
                        overscrollBehavior: 'contain',
                        borderRadius: '16px 0 0 16px',
                    }}>
                    <div
                        className="content-generator-rail-body"
                        style={{
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'visible',
                            paddingBottom: 82,
                        }}>
                    <div
                        style={{
                            padding: '1.5rem 1.75rem 1rem',
                            borderBottom: `1px solid ${C.border}`,
                        }}>
                        <div
                            style={{
                                fontFamily: "'Playfair Display',Georgia,serif",
                                fontSize: 20,
                                fontWeight: 400,
                                lineHeight: 1.2,
                            }}>
                            Content Generator
                        </div>
                        <div style={{fontSize: 11, color: C.textSoft, marginTop: 4}}>
                            1 title · 1 description · multiple bullets
                        </div>
                    </div>

                    <div
                        style={{padding: '1.25rem 1.75rem', borderBottom: `1px solid ${C.border}`}}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 10,
                            }}>
                            <Label>Profiles</Label>
                            <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                <span
                                    style={{
                                        fontSize: 9,
                                        fontWeight: 600,
                                        color: profilesSource === 'remote' ? C.teal : C.amber,
                                        background:
                                            profilesSource === 'remote' ? C.tealL : C.amberL,
                                        padding: '2px 6px',
                                        borderRadius: 5,
                                    }}>
                                    {profilesSource === 'remote' ? 'SYNCED' : 'LOCAL'}
                                </span>
                                <button
                                    onClick={() => openProfileDialog()}
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 500,
                                        color: C.amber,
                                        background: C.amberL,
                                        border: `1px solid ${C.amber}44`,
                                        padding: '3px 9px',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                    }}>
                                    + New
                                </button>
                            </div>
                        </div>

                        {(profilesLoading || profilesError) && (
                            <div
                                style={{
                                    background: profilesError ? C.amberL : C.surfaceAlt,
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 9,
                                    padding: '10px 12px',
                                    marginBottom: 10,
                                    fontSize: 11,
                                    lineHeight: 1.5,
                                    color: profilesError ? C.amber : C.textMuted,
                                }}>
                                {profilesLoading ? 'Loading saved profiles...' : profilesError}
                            </div>
                        )}

                        <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                            {profiles.map((profile) => {
                                const isActive = profile.id === activeProfileId;
                                const isModified = isActive && isDirty;

                                return (
                                    <div
                                        key={profile.id}
                                        style={{display: 'flex', flexDirection: 'column'}}>
                                        <div
                                            onClick={() => applyProfile(profile)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    applyProfile(profile);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                padding: '8px 10px',
                                                borderRadius: isModified ? '8px 8px 0 0' : 8,
                                                cursor: 'pointer',
                                                border: `1px solid ${isActive ? `${profile.color}55` : C.border}`,
                                                borderBottom: isModified ? 'none' : undefined,
                                                background: isActive
                                                    ? `${profile.color}12`
                                                    : 'transparent',
                                                transition: 'all .13s',
                                            }}>
                                            <span
                                                style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    flexShrink: 0,
                                                    display: 'block',
                                                    background: isModified
                                                        ? C.amber
                                                        : isActive
                                                          ? profile.color
                                                          : C.textFaint,
                                                }}
                                            />
                                            <div style={{flex: 1, minWidth: 0}}>
                                                <div
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: isActive ? 500 : 400,
                                                        color: isActive
                                                            ? isModified
                                                                ? C.amber
                                                                : profile.color
                                                            : C.ink,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                    {profile.name}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: 10,
                                                        color: C.textMuted,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        marginTop: 2,
                                                    }}>
                                                    {profile.settings.usecase} ·{' '}
                                                    {profile.settings.audience}
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    marginLeft: 'auto',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    flexShrink: 0,
                                                }}>
                                                {isModified ? (
                                                    <span
                                                        style={{
                                                            fontSize: 9,
                                                            fontWeight: 600,
                                                            color: C.amber,
                                                            background: C.amberL,
                                                            padding: '2px 6px',
                                                            borderRadius: 5,
                                                        }}>
                                                        ● MODIFIED
                                                    </span>
                                                ) : isActive ? (
                                                    <span
                                                        style={{
                                                            fontSize: 9,
                                                            fontWeight: 600,
                                                            color: profile.color,
                                                            background: `${profile.color}18`,
                                                            padding: '2px 6px',
                                                            borderRadius: 5,
                                                        }}>
                                                        ACTIVE
                                                    </span>
                                                ) : profile.persisted ? (
                                                    <span
                                                        style={{
                                                            fontSize: 9,
                                                            fontWeight: 600,
                                                            color: C.teal,
                                                            background: C.tealL,
                                                            padding: '2px 6px',
                                                            borderRadius: 5,
                                                        }}>
                                                        SAVED
                                                    </span>
                                                ) : null}
                                                <button
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        openProfileDialog(profile);
                                                    }}
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 500,
                                                        padding: '3px 7px',
                                                        borderRadius: 5,
                                                        border: `1px solid ${C.border}`,
                                                        background: C.surface,
                                                        color: C.textMuted,
                                                        cursor: 'pointer',
                                                        flexShrink: 0,
                                                    }}>
                                                    Edit
                                                </button>
                                                {profiles.length > 1 && (
                                                    <button
                                                        onClick={(event) =>
                                                            handleDeleteProfile(profile, event)
                                                        }
                                                        style={{
                                                            width: 18,
                                                            height: 18,
                                                            borderRadius: 4,
                                                            border: `1px solid ${C.border}`,
                                                            background: 'transparent',
                                                            color: C.textFaint,
                                                            cursor: 'pointer',
                                                            fontSize: 11,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}>
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {isModified && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: 6,
                                                    padding: '7px 10px',
                                                    background: C.amberL,
                                                    border: `1px solid ${C.amber}55`,
                                                    borderTop: `1px solid ${C.amber}33`,
                                                    borderRadius: '0 0 8px 8px',
                                                }}>
                                                <button
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        persistActiveProfile();
                                                    }}
                                                    disabled={profileActionPending}
                                                    style={{
                                                        flex: 1,
                                                        fontFamily: 'inherit',
                                                        fontSize: 11,
                                                        fontWeight: 500,
                                                        padding: '5px 8px',
                                                        borderRadius: 6,
                                                        border: 'none',
                                                        background: C.amber,
                                                        color: 'white',
                                                        cursor: profileActionPending
                                                            ? 'not-allowed'
                                                            : 'pointer',
                                                        opacity: profileActionPending ? 0.65 : 1,
                                                    }}>
                                                    {profileActionPending
                                                        ? 'Saving...'
                                                        : 'Save changes'}
                                                </button>
                                                <button
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        revertProfile();
                                                    }}
                                                    style={{
                                                        fontFamily: 'inherit',
                                                        fontSize: 11,
                                                        padding: '5px 10px',
                                                        borderRadius: 6,
                                                        border: `1px solid ${C.amber}55`,
                                                        background: 'transparent',
                                                        color: C.amber,
                                                        cursor: 'pointer',
                                                    }}>
                                                    Revert
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div style={{padding: '1.25rem 1.75rem'}}>
                        <ProductSelector />
                    </div>

                    <VarsPanel vars={vars} setVars={setVars} />

                    <div
                        style={{
                            padding: '1.25rem 1.75rem',
                            borderBottom: `1px solid ${C.border}`,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                        }}>
                        <Field label="Topic / product">
                            <div
                                onFocusCapture={() => setFocusedGeneratorInput('topic')}
                                onBlurCapture={(event) => {
                                    if (event.currentTarget.contains(event.relatedTarget)) return;
                                    setFocusedGeneratorInput((current) =>
                                        current === 'topic' ? '' : current,
                                    );
                                }}
                                style={{
                                    padding: topicInputExpanded ? '8px 10px 10px' : 0,
                                    borderRadius: 12,
                                    border: `1px solid ${topicInputExpanded ? `${C.blue}2a` : 'transparent'}`,
                                    background: topicInputExpanded ? C.surfaceAlt : 'transparent',
                                    boxShadow: topicInputExpanded
                                        ? '0 8px 24px rgba(24,95,165,.06)'
                                        : 'none',
                                    transition: 'background .15s, border-color .15s, box-shadow .15s, padding .15s',
                                }}>
                                <VarInput
                                    value={topic}
                                    onChange={handleTopicChange}
                                    placeholder="e.g. {{vendor}} {{product_name}}"
                                    vars={vars}
                                    multiline={topicInputExpanded}
                                    rows={3}
                                    showPreview={false}
                                    style={{
                                        resize: topicInputExpanded ? 'vertical' : 'none',
                                    }}
                                />
                            </div>
                            {enabled.title && (
                                <SubgroupAttributeSelector
                                    label="Generate separate title groups for selected used attributes"
                                    optionNames={titleSelectableVarNames}
                                    selectedNames={titleUniqueVarNames}
                                    onToggle={handleTitleUniqueVarToggle}
                                    emptyLabel="Use formulas in the topic to unlock title subgroup attributes here."
                                />
                            )}
                        </Field>
                        <Field label="Generate">
                            <div style={{display: 'flex', gap: 6}}>
                                {[
                                    {key: 'title', label: 'Title', color: C.blue, light: C.blueL},
                                    {
                                        key: 'desc',
                                        label: 'Description',
                                        color: C.coral,
                                        light: C.coralL,
                                    },
                                    {
                                        key: 'bullets',
                                        label: 'Bullets',
                                        color: C.teal,
                                        light: C.tealL,
                                    },
                                    {
                                        key: 'faqs',
                                        label: 'FAQs',
                                        color: C.purple,
                                        light: C.purpleL,
                                    },
                                ].map(({key, label, color, light}) => {
                                    const isEnabled = enabled[key];

                                    return (
                                        <button
                                            key={key}
                                            onClick={() => toggleEnabled(key)}
                                            style={{
                                                flex: 1,
                                                fontFamily: 'inherit',
                                                fontSize: 12,
                                                fontWeight: 500,
                                                padding: '7px 4px',
                                                borderRadius: 8,
                                                cursor: 'pointer',
                                                border: `1px solid ${isEnabled ? `${color}55` : C.border}`,
                                                background: isEnabled ? light : 'transparent',
                                                color: isEnabled ? color : C.textSoft,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 5,
                                                transition: 'all .15s',
                                            }}>
                                            <span
                                                style={{
                                                    width: 7,
                                                    height: 7,
                                                    borderRadius: '50%',
                                                    background: isEnabled ? color : C.textFaint,
                                                    flexShrink: 0,
                                                    display: 'block',
                                                }}
                                            />
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>
                        {enabled.desc && (
                            <Field label="Description context">
                                <div
                                    aria-label="Description context"
                                    style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                                    <div
                                        onFocusCapture={() => setFocusedGeneratorInput('description')}
                                        onBlurCapture={(event) => {
                                            if (event.currentTarget.contains(event.relatedTarget)) return;
                                            setFocusedGeneratorInput((current) =>
                                                current === 'description' ? '' : current,
                                            );
                                        }}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 8,
                                            padding: descriptionContextExpanded ? '8px 10px 10px' : 0,
                                            borderRadius: 12,
                                            border: `1px solid ${descriptionContextExpanded ? `${C.coral}26` : 'transparent'}`,
                                            background: descriptionContextExpanded ? C.surfaceAlt : 'transparent',
                                            boxShadow: descriptionContextExpanded
                                                ? '0 8px 24px rgba(216,90,48,.06)'
                                                : 'none',
                                            transition: 'background .15s, border-color .15s, box-shadow .15s, padding .15s',
                                        }}>
                                        <div style={{position: 'relative'}}>
                                            <VarInput
                                                value={descriptionContext}
                                                onChange={handleDescriptionContextChange}
                                                placeholder="Optional direction for this description, e.g. lead with {{finish}}"
                                                vars={vars}
                                                multiline={descriptionContextExpanded}
                                                rows={descriptionContextExpanded ? 6 : 3}
                                                showPreview={false}
                                                // Right padding reserves space for the overlay wizard
                                                // button so text doesn't flow underneath it.
                                                style={{
                                                    padding: '6px 38px 6px 10px',
                                                    resize: descriptionContextExpanded ? 'vertical' : 'none',
                                                }}
                                            />
                                            <button
                                                onClick={() => setShowDescriptionWizard((open) => !open)}
                                                title="Description wizard"
                                                aria-label="Open description wizard"
                                                aria-pressed={showDescriptionWizard}
                                                style={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    right: 6,
                                                    width: 26,
                                                    height: 26,
                                                    borderRadius: 6,
                                                    border: `1px solid ${showDescriptionWizard ? `${C.coral}55` : 'transparent'}`,
                                                    background: showDescriptionWizard
                                                        ? `${C.coral}14`
                                                        : 'transparent',
                                                    color: showDescriptionWizard ? C.coral : C.textSoft,
                                                    cursor: 'pointer',
                                                    fontSize: 13,
                                                    lineHeight: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all .15s',
                                                    zIndex: 2,
                                                }}>
                                                ✦
                                            </button>
                                        </div>

                                        {showDescriptionWizard && (
                                            <DescriptionPromptWizard
                                                apiKey={apiKey}
                                                vars={vars}
                                                brandProfile={brandProfile}
                                                product={currentProduct}
                                                onFill={handleDescriptionContextChange}
                                                onClose={() => setShowDescriptionWizard(false)}
                                            />
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 10.5,
                                            color: C.textMuted,
                                            lineHeight: 1.55,
                                        }}>
                                        Applied automatically to the description whenever you click
                                        Generate content.
                                    </div>
                                    <SubgroupAttributeSelector
                                        label="Generate separate description groups for selected used attributes"
                                        optionNames={descriptionSelectableVarNames}
                                        selectedNames={descriptionUniqueVarNames}
                                        onToggle={handleDescriptionUniqueVarToggle}
                                        emptyLabel="Use formulas in the topic or description context to unlock description subgroup attributes here."
                                    />
                                </div>
                            </Field>
                        )}
                        <Field label="AI image references">
                            <div
                                aria-label="AI image references"
                                style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                                <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                                    <button
                                        onClick={() =>
                                            setIncludeProductImage((current) => {
                                                const nextValue = !current;
                                                syncCurrentDraft({
                                                    includeProductImage: nextValue,
                                                });
                                                return nextValue;
                                            })
                                        }
                                        disabled={!currentProduct?.imageUrl}
                                        aria-pressed={includeProductImage}
                                        style={{
                                            fontFamily: 'inherit',
                                            fontSize: 11.5,
                                            fontWeight: 500,
                                            padding: '7px 10px',
                                            borderRadius: 8,
                                            border: `1px solid ${includeProductImage ? `${C.teal}44` : C.border}`,
                                            background: includeProductImage
                                                ? C.tealL
                                                : 'transparent',
                                            color: includeProductImage ? C.teal : C.textSoft,
                                            cursor: currentProduct?.imageUrl
                                                ? 'pointer'
                                                : 'not-allowed',
                                            opacity: currentProduct?.imageUrl ? 1 : 0.5,
                                        }}>
                                        {currentProduct?.imageUrl
                                            ? includeProductImage
                                                ? 'Catalog image on'
                                                : 'Use catalog image'
                                            : 'No catalog image'}
                                    </button>
                                    <button
                                        onClick={openReferenceImagePicker}
                                        style={{
                                            fontFamily: 'inherit',
                                            fontSize: 11.5,
                                            fontWeight: 500,
                                            padding: '7px 10px',
                                            borderRadius: 8,
                                            border: `1px solid ${C.border}`,
                                            background: C.surface,
                                            color: C.ink,
                                            cursor: 'pointer',
                                        }}>
                                        + Add images
                                    </button>
                                    <input
                                        ref={imagePickerInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleReferenceImageUpload}
                                        aria-label="Add AI reference images"
                                        style={{display: 'none'}}
                                    />
                                </div>
                                <div
                                    style={{
                                        fontSize: 10.5,
                                        color: C.textMuted,
                                        lineHeight: 1.55,
                                    }}>
                                    Attach the catalog photo or upload extra angles so AI can use
                                    visible finish, shape, and hardware details as grounding.
                                </div>
                                {activeReferenceImages.length ? (
                                    <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                                        {activeReferenceImages.map((image) => (
                                            <div
                                                key={image.id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    padding: 8,
                                                    borderRadius: 10,
                                                    border: `1px solid ${C.border}`,
                                                    background: C.surfaceAlt,
                                                }}>
                                                <img
                                                    src={image.url}
                                                    alt={image.name || 'AI reference'}
                                                    style={{
                                                        width: 46,
                                                        height: 46,
                                                        objectFit: 'cover',
                                                        borderRadius: 8,
                                                        border: `1px solid ${C.border}`,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <div style={{flex: 1, minWidth: 0}}>
                                                    <div
                                                        style={{
                                                            fontSize: 11.5,
                                                            color: C.ink,
                                                            fontWeight: 500,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}>
                                                        {image.name || 'Reference image'}
                                                    </div>
                                                    <div
                                                        style={{
                                                            fontSize: 10,
                                                            color: C.textMuted,
                                                            marginTop: 3,
                                                        }}>
                                                        {image.source === 'catalog'
                                                            ? 'Catalog image'
                                                            : 'Uploaded reference'}
                                                    </div>
                                                </div>
                                                {image.source === 'upload' ? (
                                                    <button
                                                        onClick={() =>
                                                            removeReferenceImage(image.id)
                                                        }
                                                        style={{
                                                            fontFamily: 'inherit',
                                                            fontSize: 10.5,
                                                            fontWeight: 500,
                                                            padding: '5px 8px',
                                                            borderRadius: 6,
                                                            border: `1px solid ${C.border}`,
                                                            background: 'transparent',
                                                            color: C.textMuted,
                                                            cursor: 'pointer',
                                                        }}>
                                                        Remove
                                                    </button>
                                                ) : (
                                                    <span
                                                        style={{
                                                            fontSize: 9.5,
                                                            fontWeight: 600,
                                                            color: C.teal,
                                                            background: C.tealL,
                                                            border: `1px solid ${C.teal}33`,
                                                            padding: '4px 7px',
                                                            borderRadius: 999,
                                                            flexShrink: 0,
                                                        }}>
                                                        Auto
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            fontSize: 10.5,
                                            color: C.textMuted,
                                            padding: '10px 12px',
                                            borderRadius: 10,
                                            border: `1px dashed ${C.border2}`,
                                            background: C.surfaceAlt,
                                        }}>
                                        No image references attached for this SKU yet.
                                    </div>
                                )}
                            </div>
                        </Field>
                    </div>

                    {(enabled.bullets || enabled.faqs) && (
                        <div style={{padding: '1.25rem 1.75rem'}}>
                            <Field
                                label={
                                    enabled.faqs
                                        ? 'FAQ topics'
                                        : 'Bullet fields & keywords'
                                }>
                                {enabled.faqs && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            marginBottom: 8,
                                            padding: '6px 10px',
                                            background: C.purpleL,
                                            border: `1px solid ${C.purple}33`,
                                            borderRadius: 8,
                                        }}>
                                        <span
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color: C.purple,
                                                textTransform: 'uppercase',
                                                letterSpacing: '.05em',
                                            }}>
                                            FAQs to generate
                                        </span>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                marginLeft: 'auto',
                                            }}>
                                            <button
                                                onClick={() => updateFaqCount(faqCount - 1)}
                                                disabled={faqCount <= 1}
                                                style={{
                                                    width: 22,
                                                    height: 22,
                                                    borderRadius: 5,
                                                    border: `1px solid ${C.purple}44`,
                                                    background: C.surface,
                                                    color: C.purple,
                                                    cursor: faqCount > 1 ? 'pointer' : 'not-allowed',
                                                    opacity: faqCount > 1 ? 1 : 0.4,
                                                    fontFamily: 'inherit',
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                }}>
                                                −
                                            </button>
                                            <span
                                                style={{
                                                    minWidth: 22,
                                                    textAlign: 'center',
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    color: C.purple,
                                                }}>
                                                {faqCount}
                                            </span>
                                            <button
                                                onClick={() => updateFaqCount(faqCount + 1)}
                                                disabled={faqCount >= 10}
                                                style={{
                                                    width: 22,
                                                    height: 22,
                                                    borderRadius: 5,
                                                    border: `1px solid ${C.purple}44`,
                                                    background: C.surface,
                                                    color: C.purple,
                                                    cursor: faqCount < 10 ? 'pointer' : 'not-allowed',
                                                    opacity: faqCount < 10 ? 1 : 0.4,
                                                    fontFamily: 'inherit',
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                }}>
                                                +
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div
                                    className="bullet-editor-grid bullet-editor-grid--header"
                                    style={{
                                        padding: '0 1px',
                                        marginBottom: 4,
                                    }}>
                                    <span className="bullet-editor-heading bullet-editor-heading--ghost">
                                        #
                                    </span>
                                    <span className="bullet-editor-heading bullet-editor-heading--field">
                                        Field / angle
                                    </span>
                                    <span className="bullet-editor-heading bullet-editor-heading--keywords">
                                        Keywords
                                    </span>
                                    <span className="bullet-editor-heading bullet-editor-heading--ghost">
                                        *
                                    </span>
                                    <span className="bullet-editor-heading bullet-editor-heading--ghost">
                                        x
                                    </span>
                                </div>
                                <div
                                    style={{
                                        fontSize: 9.5,
                                        color: C.textMuted,
                                        lineHeight: 1.5,
                                        marginBottom: 8,
                                        maxWidth: 520,
                                    }}>
                                    Bullets stay shared across the selected group by default. Focus
                                    a row to open more space for reviewing that row&apos;s keywords.
                                </div>
                                <div style={{display: 'flex', flexDirection: 'column', gap: 0}}>
                                    {rows.map((row, index) => {
                                        const wizardOpen = activeWizard === row.id;
                                        const rowSelectableVarNames = extractSelectableFormulaVarNames([
                                            row.field || '',
                                            row.kw || '',
                                        ]);
                                        const rowSubgroupOpen =
                                            openBulletSubgroupRowId === row.id;
                                        const isExpandedRow =
                                            focusedBulletRowId === row.id
                                            || wizardOpen
                                            || rowSubgroupOpen;

                                        return (
                                            <div
                                                key={row.id}
                                                onFocusCapture={() => {
                                                    setFocusedBulletRowId((current) =>
                                                        current === row.id ? current : row.id,
                                                    );
                                                }}
                                                onBlurCapture={(event) => {
                                                    if (event.currentTarget.contains(event.relatedTarget)) {
                                                        return;
                                                    }
                                                    setFocusedBulletRowId((current) =>
                                                        current === row.id ? null : current,
                                                    );
                                                }}
                                                style={{
                                                    marginBottom: wizardOpen ? 0 : 8,
                                                    padding: isExpandedRow ? '8px 10px 10px' : 0,
                                                    borderRadius: 12,
                                                    border: `1px solid ${isExpandedRow ? `${C.blue}2a` : 'transparent'}`,
                                                    background: isExpandedRow ? C.surfaceAlt : 'transparent',
                                                    boxShadow: isExpandedRow
                                                        ? '0 8px 24px rgba(24,95,165,.06)'
                                                        : 'none',
                                                    transition: 'background .15s, border-color .15s, box-shadow .15s, padding .15s',
                                                }}>
                                                {isExpandedRow ? (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 8,
                                                            // Was `0` when wizardOpen, which pinned
                                                            // the BulletSubgroupControl ("Variation")
                                                            // flush against the wizard panel below.
                                                            // Give it visual separation.
                                                            marginBottom: wizardOpen ? 10 : 4,
                                                        }}>
                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: '20px minmax(0, 1fr) auto',
                                                                gap: 8,
                                                                alignItems: 'start',
                                                            }}>
                                                            <div
                                                                style={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    borderRadius: '50%',
                                                                    background: C.surfaceRaised,
                                                                    border: `1px solid ${C.border}`,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: 9,
                                                                    fontWeight: 500,
                                                                    color: C.textFaint,
                                                                    flexShrink: 0,
                                                                    marginTop: 4,
                                                                }}>
                                                                {index + 1}
                                                            </div>
                                                            <div className="bullet-editor-field">
                                                                <VarInput
                                                                    value={row.field}
                                                                    onChange={(value) =>
                                                                        updateRow(row.id, 'field', value)
                                                                    }
                                                                    placeholder="e.g. Key benefit"
                                                                    vars={vars}
                                                                    showPreview={false}
                                                                    style={{
                                                                        borderColor: wizardOpen
                                                                            ? 'rgba(83,74,183,.35)'
                                                                            : undefined,
                                                                        background: wizardOpen
                                                                            ? 'rgba(83,74,183,.03)'
                                                                            : undefined,
                                                                    }}
                                                                />
                                                            </div>
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 6,
                                                                    justifySelf: 'end',
                                                                }}>
                                                                <button
                                                                    onClick={() =>
                                                                        setActiveWizard(wizardOpen ? null : row.id)
                                                                    }
                                                                    title="Prompt wizard"
                                                                    style={{
                                                                        width: 26,
                                                                        height: 26,
                                                                        borderRadius: 6,
                                                                        border: `1px solid ${wizardOpen ? `${C.purple}88` : C.border}`,
                                                                        background: wizardOpen
                                                                            ? `${C.purple}12`
                                                                            : 'transparent',
                                                                        color: wizardOpen ? C.purple : C.textSoft,
                                                                        cursor: 'pointer',
                                                                        fontSize: 13,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flexShrink: 0,
                                                                        transition: 'all .15s',
                                                                    }}>
                                                                    ✦
                                                                </button>
                                                                <button
                                                                    onClick={() => removeRow(row.id)}
                                                                    style={delStyle}>
                                                                    ×
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div style={{paddingLeft: 28}}>
                                                            <div className="bullet-editor-keywords">
                                                                <VarInput
                                                                    value={row.kw}
                                                                    onChange={(value) =>
                                                                        updateRow(row.id, 'kw', value)
                                                                    }
                                                                    placeholder="keywords, {{var}}"
                                                                    vars={vars}
                                                                    multiline
                                                                    rows={4}
                                                                    showPreview={false}
                                                                    style={{
                                                                        borderColor: wizardOpen
                                                                            ? 'rgba(83,74,183,.35)'
                                                                            : undefined,
                                                                        background: wizardOpen
                                                                            ? 'rgba(83,74,183,.03)'
                                                                            : undefined,
                                                                        resize: 'vertical',
                                                                    }}
                                                                />
                                                                <BulletSubgroupControl
                                                                    optionNames={rowSelectableVarNames}
                                                                    selectedNames={row.uniqueVarNames || []}
                                                                    open={rowSubgroupOpen}
                                                                    onToggleOpen={() =>
                                                                        setOpenBulletSubgroupRowId(
                                                                            rowSubgroupOpen ? null : row.id,
                                                                        )
                                                                    }
                                                                    onToggle={(varName) =>
                                                                        toggleRowUniqueVarName(
                                                                            row.id,
                                                                            varName,
                                                                        )
                                                                    }
                                                                    onClear={() =>
                                                                        {
                                                                            setBulletUniqueVarNamesByIndex(
                                                                                (currentNamesByIndex) => {
                                                                                    const nextNamesByIndex =
                                                                                        normalizeUniqueVarNamesByIndex(
                                                                                            currentNamesByIndex,
                                                                                        );
                                                                                    nextNamesByIndex[index] =
                                                                                        [];
                                                                                    return nextNamesByIndex;
                                                                                },
                                                                            );
                                                                            setRowsWithDraft((currentRows) =>
                                                                                currentRows.map(
                                                                                    (candidateRow, candidateIndex) =>
                                                                                        candidateIndex ===
                                                                                        index
                                                                                            ? {
                                                                                                  ...candidateRow,
                                                                                                  uniqueVarNames:
                                                                                                      [],
                                                                                              }
                                                                                            : candidateRow,
                                                                                ),
                                                                            );
                                                                        }
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="bullet-editor-grid bullet-editor-grid--row"
                                                        style={{
                                                            alignItems: 'flex-start',
                                                            // Same rationale as the expanded variant:
                                                            // leave breathing room above the wizard
                                                            // panel when it's open.
                                                            marginBottom: wizardOpen ? 10 : 4,
                                                        }}>
                                                        <div
                                                            style={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '50%',
                                                                background: C.surfaceRaised,
                                                                border: `1px solid ${C.border}`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 9,
                                                                fontWeight: 500,
                                                                color: C.textFaint,
                                                                flexShrink: 0,
                                                                marginTop: 4,
                                                            }}>
                                                            {index + 1}
                                                        </div>
                                                        <div className="bullet-editor-field">
                                                            <VarInput
                                                                value={row.field}
                                                                onChange={(value) =>
                                                                    updateRow(row.id, 'field', value)
                                                                }
                                                                placeholder="e.g. Key benefit"
                                                                vars={vars}
                                                                showPreview={false}
                                                                style={{
                                                                    borderColor: wizardOpen
                                                                        ? 'rgba(83,74,183,.35)'
                                                                        : undefined,
                                                                    background: wizardOpen
                                                                        ? 'rgba(83,74,183,.03)'
                                                                        : undefined,
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="bullet-editor-keywords">
                                                            <VarInput
                                                                value={row.kw}
                                                                onChange={(value) =>
                                                                    updateRow(row.id, 'kw', value)
                                                                }
                                                                placeholder="keywords, {{var}}"
                                                                vars={vars}
                                                                showPreview={false}
                                                                style={{
                                                                    borderColor: wizardOpen
                                                                        ? 'rgba(83,74,183,.35)'
                                                                        : undefined,
                                                                    background: wizardOpen
                                                                        ? 'rgba(83,74,183,.03)'
                                                                        : undefined,
                                                                }}
                                                            />
                                                            <BulletSubgroupControl
                                                                optionNames={rowSelectableVarNames}
                                                                selectedNames={row.uniqueVarNames || []}
                                                                open={rowSubgroupOpen}
                                                                onToggleOpen={() =>
                                                                    setOpenBulletSubgroupRowId(
                                                                        rowSubgroupOpen ? null : row.id,
                                                                    )
                                                                }
                                                                onToggle={(varName) =>
                                                                    toggleRowUniqueVarName(
                                                                        row.id,
                                                                        varName,
                                                                    )
                                                                }
                                                                onClear={() =>
                                                                    {
                                                                        setBulletUniqueVarNamesByIndex(
                                                                            (currentNamesByIndex) => {
                                                                                const nextNamesByIndex =
                                                                                    normalizeUniqueVarNamesByIndex(
                                                                                        currentNamesByIndex,
                                                                                    );
                                                                                nextNamesByIndex[index] =
                                                                                    [];
                                                                                return nextNamesByIndex;
                                                                            },
                                                                        );
                                                                        setRowsWithDraft((currentRows) =>
                                                                            currentRows.map(
                                                                                (candidateRow, candidateIndex) =>
                                                                                    candidateIndex ===
                                                                                    index
                                                                                        ? {
                                                                                              ...candidateRow,
                                                                                              uniqueVarNames:
                                                                                                  [],
                                                                                          }
                                                                                        : candidateRow,
                                                                            ),
                                                                        );
                                                                    }
                                                                }
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={() =>
                                                                setActiveWizard(wizardOpen ? null : row.id)
                                                            }
                                                            title="Prompt wizard"
                                                            style={{
                                                                width: 26,
                                                                height: 26,
                                                                borderRadius: 6,
                                                                border: `1px solid ${wizardOpen ? `${C.purple}88` : C.border}`,
                                                                background: wizardOpen
                                                                    ? C.purpleL
                                                                    : 'transparent',
                                                                color: wizardOpen ? C.purple : C.textSoft,
                                                                cursor: 'pointer',
                                                                fontSize: 13,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                                transition: 'all .15s',
                                                                marginTop: 1,
                                                            }}>
                                                            ✦
                                                        </button>
                                                        <button
                                                            onClick={() => removeRow(row.id)}
                                                            style={{
                                                                ...delStyle,
                                                                marginTop: 3,
                                                            }}>
                                                            ×
                                                        </button>
                                                    </div>
                                                )}
                                                {wizardOpen && (
                                                    <PromptWizard
                                                        apiKey={apiKey}
                                                        brandProfile={brandProfile}
                                                        vars={vars}
                                                        product={currentProduct}
                                                        wizardMode={enabled.faqs ? 'faqs' : 'bullets'}
                                                        onFill={({field, kw}) => {
                                                            updateRow(row.id, 'field', field);
                                                            updateRow(row.id, 'kw', kw);
                                                        }}
                                                        onClose={() => setActiveWizard(null)}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 6,
                                        marginTop: 6,
                                    }}>
                                    <button
                                        onClick={addRow}
                                        style={{
                                            flex: '1 1 140px',
                                            fontSize: 12,
                                            padding: 7,
                                            borderRadius: 7,
                                            border: `1px dashed ${C.border2}`,
                                            background: 'transparent',
                                            color: C.textMuted,
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                        }}>
                                        + Add {enabled.faqs ? 'FAQ topic' : 'field'}
                                    </button>
                                    {enabled.faqs && (
                                        <button
                                            onClick={suggestFaqTopics}
                                            disabled={suggestingFaqTopics}
                                            style={{
                                                fontSize: 12,
                                                padding: '7px 12px',
                                                borderRadius: 7,
                                                border: `1px solid ${C.purple}44`,
                                                background: suggestingFaqTopics
                                                    ? C.purpleL
                                                    : C.purple,
                                                color: suggestingFaqTopics
                                                    ? C.purple
                                                    : C.surface,
                                                fontWeight: 600,
                                                cursor: suggestingFaqTopics
                                                    ? 'wait'
                                                    : 'pointer',
                                                whiteSpace: 'nowrap',
                                                opacity: suggestingFaqTopics ? 0.7 : 1,
                                            }}>
                                            {suggestingFaqTopics
                                                ? 'Suggesting…'
                                                : '✨ Suggest topics'}
                                        </button>
                                    )}
                                </div>
                            </Field>
                        </div>
                    )}
                    </div>

                    <div
                        className="content-generator-rail-footer"
                        style={{
                            position: generatorRailFrame.width ? 'fixed' : 'sticky',
                            left: generatorRailFrame.width
                                ? generatorRailFrame.left
                                : undefined,
                            bottom: 0,
                            width: generatorRailFrame.width
                                ? generatorRailFrame.width
                                : undefined,
                            boxSizing: 'border-box',
                            zIndex: 3,
                            padding: '12px 16px',
                            borderTop: `1px solid ${C.border}`,
                            background: C.surface,
                            backdropFilter: 'blur(10px)',
                            flexShrink: 0,
                            boxShadow: '0 -8px 18px rgba(17,24,39,.06)',
                        }}>
                        {productsError && (
                            <div
                                style={{
                                    fontSize: 11,
                                    padding: '7px 10px',
                                    borderRadius: 7,
                                    background: C.amberL,
                                    color: C.amber,
                                    border: `1px solid ${C.amber}33`,
                                    marginBottom: 10,
                                }}>
                                {productsError}
                            </div>
                        )}
                        {generationError && (
                            <div
                                style={{
                                    fontSize: 11.5,
                                    padding: '7px 10px',
                                    borderRadius: 7,
                                    background: `${C.coral}18`,
                                    color: '#a32d2d',
                                    border: '1px solid rgba(226,75,74,.2)',
                                    marginBottom: 10,
                                }}>
                                {generationError}
                            </div>
                        )}
                        <button
                            onClick={generate}
                            disabled={aiLoading}
                            style={{
                                width: '100%',
                                fontFamily: 'inherit',
                                fontSize: 13,
                                fontWeight: 600,
                                padding: '10px 16px',
                                borderRadius: 10,
                                border: 'none',
                                outline: 'none',
                                background: '#1D9E75',
                                color: '#fff',
                                cursor: aiLoading ? 'not-allowed' : 'pointer',
                                opacity: aiLoading ? 0.5 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                letterSpacing: '.01em',
                            }}>
                            {isFullGenerating && <Spinner />}
                            {isFullGenerating
                                ? 'Generating...'
                                : selectedProducts.length > 1
                                  ? `Generate ${selectedProducts.length} SKUs`
                                  : 'Generate content'}
                        </button>
                    </div>
                </div>

                <ResizeHandle
                    onStartDrag={startGeneratorRailDrag}
                    color={C.teal}
                    ariaLabel="Resize generator rail"
                    className="content-generator-rail-handle"
                />

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100vh',
                        background: C.mist,
                    }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0 1.5rem',
                            height: 52,
                            borderBottom: `1px solid ${C.border}`,
                            background: C.surface,
                            flexShrink: 0,
                        }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 6, minWidth: 0}}>
                            {activeProfile && (
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 500,
                                        color: activeProfile.color,
                                        background: `${activeProfile.color}15`,
                                        border: `1px solid ${activeProfile.color}33`,
                                        padding: '3px 10px',
                                        borderRadius: 20,
                                    }}>
                                    {activeProfile.name}
                                </span>
                            )}
                            <div
                                style={{
                                    width: 1,
                                    height: 16,
                                    background: C.border,
                                    margin: '0 4px',
                                }}
                            />
                            <Badge
                                color={C.blue}
                                label="Title"
                                count={enabled.title && currentResult?.title ? 1 : 0}
                            />
                            <Badge
                                color={C.coral}
                                label="Description"
                                count={enabled.desc && currentResult?.desc ? 1 : 0}
                            />
                            <Badge
                                color={C.teal}
                                label="Bullets"
                                count={enabled.bullets ? (currentResult?.bullets?.length ?? 0) : 0}
                            />
                            <Badge
                                color={C.purple}
                                label="FAQs"
                                count={enabled.faqs ? (currentResult?.faqs?.length ?? 0) : 0}
                            />
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0}}>
                            {!!selectedProducts.length && (
                                <>
                                    <span
                                        style={{
                                            fontSize: 10.5,
                                            fontWeight: 600,
                                            color: C.textMuted,
                                        }}>
                                        SKU{' '}
                                        {Math.min(currentProductIndex + 1, selectedProducts.length)}{' '}
                                        of {selectedProducts.length}
                                    </span>
                                    <div style={{display: 'flex', gap: 4}}>
                                        <button
                                            onClick={() =>
                                                setCurrentProductIndex((index) =>
                                                    Math.max(0, index - 1),
                                                )
                                            }
                                            disabled={currentProductIndex <= 0}
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 500,
                                                color: C.textMuted,
                                                background: C.surface,
                                                border: `1px solid ${C.border}`,
                                                padding: '5px 10px',
                                                borderRadius: 7,
                                                cursor:
                                                    currentProductIndex <= 0
                                                        ? 'not-allowed'
                                                        : 'pointer',
                                                opacity: currentProductIndex <= 0 ? 0.45 : 1,
                                                fontFamily: 'inherit',
                                            }}>
                                            Prev
                                        </button>
                                        <button
                                            onClick={() =>
                                                setCurrentProductIndex((index) =>
                                                    Math.min(
                                                        selectedProducts.length - 1,
                                                        index + 1,
                                                    ),
                                                )
                                            }
                                            disabled={
                                                currentProductIndex >= selectedProducts.length - 1
                                            }
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 500,
                                                color: C.textMuted,
                                                background: C.surface,
                                                border: `1px solid ${C.border}`,
                                                padding: '5px 10px',
                                                borderRadius: 7,
                                                cursor:
                                                    currentProductIndex >=
                                                    selectedProducts.length - 1
                                                        ? 'not-allowed'
                                                        : 'pointer',
                                                opacity:
                                                    currentProductIndex >=
                                                    selectedProducts.length - 1
                                                        ? 0.45
                                                        : 1,
                                                fontFamily: 'inherit',
                                            }}>
                                            Next
                                        </button>
                                    </div>
                                </>
                            )}
                            {currentResult && (
                                <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                                    <button
                                        onClick={copyAll}
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 500,
                                            color: C.teal,
                                            background: C.tealL,
                                            border: `1px solid ${C.teal}44`,
                                            padding: '5px 14px',
                                            borderRadius: 7,
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                        }}>
                                        {copied[`${currentProduct?.id || 'product'}-all`]
                                            ? 'Copied!'
                                            : 'Copy all'}
                                    </button>
                                    <button
                                        onClick={() => exportResults('xlsx')}
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 500,
                                            color: C.purple,
                                            background: C.purpleL,
                                            border: `1px solid ${C.purple}44`,
                                            padding: '5px 14px',
                                            borderRadius: 7,
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                        }}>
                                        Export
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{display: 'flex'}}>
                        <div
                            style={{
                                flex: 1,
                                overflow: 'visible',
                                padding: '1.5rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                                minWidth: 0,
                            }}>
                            {currentProduct && (
                                <div
                                    style={{
                                        background: C.surface,
                                        border: `1px solid ${C.border}`,
                                        borderRadius: 14,
                                        padding: '16px',
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(180px, 220px) minmax(0, 1fr)',
                                        gap: 16,
                                        animation: 'popIn .2s ease both',
                                    }}>
                                    <div
                                        style={{
                                            minHeight: 180,
                                            borderRadius: 12,
                                            overflow: 'hidden',
                                            border: `1px solid ${C.border}`,
                                            background:
                                                'linear-gradient(135deg, rgba(29,158,117,.1), rgba(24,95,165,.08))',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        {currentProduct.imageUrl ? (
                                            <img
                                                src={currentProduct.imageUrl}
                                                alt={currentProduct.name}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    color: C.textMuted,
                                                }}>
                                                <div
                                                    style={{
                                                        width: 54,
                                                        height: 54,
                                                        borderRadius: '50%',
                                                        background: 'rgba(255,255,255,.12)',
                                                        border: `1px solid ${C.border}`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 22,
                                                    }}>
                                                    ◌
                                                </div>
                                                <span
                                                    style={{
                                                        fontSize: 11.5,
                                                        fontWeight: 500,
                                                    }}>
                                                    No image available
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        style={{
                                            minWidth: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 12,
                                        }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                flexWrap: 'wrap',
                                            }}>
                                            <span
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    color: C.teal,
                                                    background: C.tealL,
                                                    border: `1px solid ${C.teal}33`,
                                                    padding: '4px 8px',
                                                    borderRadius: 999,
                                                    letterSpacing: '.04em',
                                                    textTransform: 'uppercase',
                                                }}>
                                                {currentProduct.sku}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    color: C.textMuted,
                                                    background: C.surfaceRaised,
                                                    border: `1px solid ${C.border}`,
                                                    padding: '4px 8px',
                                                    borderRadius: 999,
                                                    letterSpacing: '.04em',
                                                    textTransform: 'uppercase',
                                                }}>
                                                {productSource}
                                            </span>
                                            {currentProductReviewGroup ? (
                                                <ProductGroupPicker
                                                    currentProduct={currentProduct}
                                                    selectedProducts={selectedProducts}
                                                    reviewGroupByProductId={reviewGroupByProductId}
                                                    setReviewGroupForProducts={setReviewGroupForProducts}
                                                />
                                            ) : null}
                                        </div>

                                        <div>
                                            <div
                                                style={{
                                                    fontFamily: "'Playfair Display',Georgia,serif",
                                                    fontSize: 24,
                                                    lineHeight: 1.15,
                                                    color: C.ink,
                                                }}>
                                                {currentProduct.name}
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 6,
                                                    fontSize: 12,
                                                    color: C.textMuted,
                                                    lineHeight: 1.6,
                                                }}>
                                                {currentProduct.vendor} · {currentProduct.collection || 'All'} ·{' '}
                                                {currentProduct.category}
                                            </div>
                                        </div>

                                        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 10,
                                                }}>
                                                <div
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        color: C.textFaint,
                                                        letterSpacing: '.07em',
                                                        textTransform: 'uppercase',
                                                    }}>
                                                    Details
                                                </div>
                                                <button
                                                    onClick={() => setProductFieldConfigOpen(true)}
                                                    style={{
                                                        fontFamily: 'inherit',
                                                        border: `1px solid ${C.border}`,
                                                        background: C.surfaceRaised,
                                                        color: C.textSoft,
                                                        borderRadius: 8,
                                                        padding: '5px 9px',
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        cursor: 'pointer',
                                                    }}>
                                                    Fields
                                                </button>
                                            </div>

                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                                    gap: 10,
                                                }}>
                                                {currentProductDetails.length ? (
                                                    currentProductDetails.map((detail) => (
                                                        <div
                                                            key={detail.label}
                                                            style={{
                                                                padding: '10px 11px',
                                                                borderRadius: 10,
                                                                background: C.surfaceAlt,
                                                                border: `1px solid ${C.border}`,
                                                            }}>
                                                            <div
                                                                style={{
                                                                    fontSize: 9.5,
                                                                    fontWeight: 600,
                                                                    color: C.textFaint,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '.07em',
                                                                    marginBottom: 4,
                                                                }}>
                                                                {detail.label}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize: 12.5,
                                                                    color: C.ink,
                                                                    lineHeight: 1.5,
                                                                    wordBreak: 'break-word',
                                                                }}>
                                                                {detail.value}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div
                                                        style={{
                                                            gridColumn: '1 / -1',
                                                            fontSize: 12,
                                                            color: C.textMuted,
                                                            lineHeight: 1.6,
                                                        }}>
                                                        Product details are limited for this SKU, but
                                                        variable data is still available in the editor.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <ProductDetailFieldsDialog
                                availableVars={currentProduct?.vars?.length ? currentProduct.vars : vars}
                                error={productDetailFieldSettings.error}
                                fields={productDetailFieldSettings.fields}
                                onClose={() => setProductFieldConfigOpen(false)}
                                onSave={productDetailFieldSettings.saveSettings}
                                open={productFieldConfigOpen}
                                saving={productDetailFieldSettings.saving}
                            />

                            {!currentResult && !isFullGenerating && (
                                <EmptyState
                                    hasProduct={Boolean(currentProduct)}
                                    productName={currentProduct?.name || currentProduct?.sku || ''}
                                    selectedCount={selectedProducts.length}
                                    enabled={enabled}
                                    tone={tone}
                                    useCase={usecase}
                                    audience={audience}
                                />
                            )}
                            {isFullGenerating && (
                                <div
                                    style={{
                                        border: `1px solid ${C.border}`,
                                        background: `linear-gradient(135deg, ${C.surface} 0%, ${C.surfaceAlt} 100%)`,
                                        borderRadius: 14,
                                        padding: '16px 18px',
                                        display: 'grid',
                                        gap: 12,
                                        animation: 'popIn .2s ease both',
                                    }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            minWidth: 0,
                                        }}>
                                        <div
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: '50%',
                                                border: `1px solid ${C.border}`,
                                                background: C.surface,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                            <Spinner dark />
                                        </div>
                                        <div style={{minWidth: 0}}>
                                            <div
                                                style={{
                                                    fontSize: 13.5,
                                                    fontWeight: 700,
                                                    color: C.ink,
                                                }}>
                                                Generating content
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 11.5,
                                                    color: C.textSoft,
                                                    lineHeight: 1.55,
                                                    marginTop: 2,
                                                }}>
                                                {generationStatusLabel}
                                            </div>
                                        </div>
                                    </div>
                                    {batchGenerationState && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 8,
                                            }}>
                                            {[
                                                `${generationProgress}% complete`,
                                                `${batchGenerationState.stepIndex}/${batchGenerationState.totalSteps} steps`,
                                                `${batchGenerationState.totalSkus} SKU${batchGenerationState.totalSkus === 1 ? '' : 's'}`,
                                                batchGenerationState.scopeLabel || null,
                                            ]
                                                .filter(Boolean)
                                                .map((item) => (
                                                    <span
                                                        key={item}
                                                        style={{
                                                            fontSize: 10.5,
                                                            fontWeight: 600,
                                                            color: C.textMuted,
                                                            background: C.surface,
                                                            border: `1px solid ${C.border}`,
                                                            borderRadius: 999,
                                                            padding: '4px 9px',
                                                        }}>
                                                        {item}
                                                    </span>
                                                ))}
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            height: 9,
                                            borderRadius: 999,
                                            background: C.surface,
                                            border: `1px solid ${C.border}`,
                                            overflow: 'hidden',
                                        }}>
                                        <div
                                            style={{
                                                width: `${Math.max(generationProgress, aiLoading ? 8 : 0)}%`,
                                                height: '100%',
                                                borderRadius: 999,
                                                background: `linear-gradient(90deg, ${C.teal}, ${C.blue})`,
                                                transition: 'width .25s ease',
                                            }}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 10.5,
                                            color: C.textMuted,
                                            lineHeight: 1.5,
                                        }}>
                                        Generated sections appear below as each step finishes, so you
                                        can review the copy before the batch completes.
                                    </div>
                                </div>
                            )}
                            {currentResult && (
                                <>
                                    {/* ── Title ── */}
                                    {enabled.title &&
                                        currentResult.title &&
                                        (editingField === 'title' ? (
                                            <FieldCard
                                                key={`${currentProduct?.id}-title`}
                                                apiKey={apiKey}
                                                charLimit={0}
                                                customCommands={customCommands}
                                                field={{
                                                    id: 'gen-title',
                                                    type: 'title',
                                                    label: 'Title',
                                                    text: getEditModeText(
                                                        currentResult.title,
                                                        currentGenerationHighlights.title,
                                                    ),
                                                    maxChars: fieldCardMaxChars.title,
                                                    status:
                                                        resultFieldMeta['gen-title']?.status ||
                                                        'draft',
                                                    history:
                                                        resultFieldMeta['gen-title']?.history || [],
                                                    searchTerms:
                                                        resultFieldMeta['gen-title']?.searchTerms ||
                                                        [],
                                                }}
                                                highlightSubstrings={
                                                    currentGenerationHighlights.title
                                                }
                                                onDelete={() => setEditingField(null)}
                                                onOpenChat={() =>
                                                    setChatFieldId((prev) =>
                                                        prev === 'gen-title' ? null : 'gen-title',
                                                    )
                                                }
                                                onRegenerate={regenerateResultField}
                                                chatOpen={chatFieldId === 'gen-title'}
                                                onUpdate={handleResultFieldUpdate}
                                                vars={vars}
                                            />
                                        ) : (
                                            <OutCard
                                                label="Title"
                                                dotColor={C.blue}
                                                labelColor={C.blue}
                                                delay={0}
                                                actions={
                                                    <>
                                                        <button
                                                            onClick={() => setEditingField('title')}
                                                            style={actionBtnPrimaryStyle}>
                                                            Edit
                                                        </button>
                                                        <CopyBtn
                                                            inline
                                                            id={`${currentProduct?.id || 'product'}-title`}
                                                            text={currentResult.title}
                                                            copied={copied}
                                                            onCopy={copy}
                                                        />
                                                    </>
                                                }>
                                                <div
                                                    onDoubleClick={() => setEditingField('title')}
                                                    title="Double-click to edit"
                                                    style={{
                                                        fontFamily:
                                                            "'Playfair Display',Georgia,serif",
                                                        fontSize: 18,
                                                        fontWeight: 400,
                                                        color: C.ink,
                                                        lineHeight: 1.35,
                                                        cursor: 'text',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }}>
                                                    <ResolvedText
                                                        text={currentResult.title}
                                                        vars={vars}
                                                        highlightSubstrings={
                                                            currentGenerationHighlights.title
                                                        }
                                                    />
                                                </div>
                                            </OutCard>
                                        ))}

                                    {/* ── Description ── */}
                                    {enabled.desc &&
                                        currentResult.desc &&
                                        (editingField === 'desc' ? (
                                            <FieldCard
                                                key={`${currentProduct?.id}-desc`}
                                                apiKey={apiKey}
                                                charLimit={0}
                                                customCommands={customCommands}
                                                field={{
                                                    id: 'gen-desc',
                                                    type: 'description',
                                                    label: 'Description',
                                                    text: getEditModeText(
                                                        currentResult.desc,
                                                        currentGenerationHighlights.description,
                                                    ),
                                                    maxChars: fieldCardMaxChars.description,
                                                    status:
                                                        resultFieldMeta['gen-desc']?.status ||
                                                        'draft',
                                                    history:
                                                        resultFieldMeta['gen-desc']?.history || [],
                                                    searchTerms:
                                                        resultFieldMeta['gen-desc']?.searchTerms ||
                                                        [],
                                                }}
                                                highlightSubstrings={
                                                    currentGenerationHighlights.description
                                                }
                                                onDelete={() => setEditingField(null)}
                                                onOpenChat={() =>
                                                    setChatFieldId((prev) =>
                                                        prev === 'gen-desc' ? null : 'gen-desc',
                                                    )
                                                }
                                                onRegenerate={regenerateResultField}
                                                chatOpen={chatFieldId === 'gen-desc'}
                                                onUpdate={handleResultFieldUpdate}
                                                vars={vars}
                                            />
                                        ) : (
                                            <OutCard
                                                label="Description"
                                                dotColor={C.coral}
                                                labelColor={C.coral}
                                                delay={1}
                                                actions={
                                                    <>
                                                        <button
                                                            onClick={() => setEditingField('desc')}
                                                            style={actionBtnPrimaryStyle}>
                                                            Edit
                                                        </button>
                                                        <CopyBtn
                                                            inline
                                                            id={`${currentProduct?.id || 'product'}-desc`}
                                                            text={currentResult.desc}
                                                            copied={copied}
                                                            onCopy={copy}
                                                        />
                                                    </>
                                                }>
                                                <div
                                                    onDoubleClick={() => setEditingField('desc')}
                                                    title="Double-click to edit"
                                                    style={{
                                                        fontSize: 13.5,
                                                        color: C.ink,
                                                        lineHeight: 1.7,
                                                        cursor: 'text',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }}>
                                                    <ResolvedText
                                                        text={currentResult.desc}
                                                        vars={vars}
                                                        highlightSubstrings={
                                                            currentGenerationHighlights.description
                                                        }
                                                    />
                                                </div>
                                            </OutCard>
                                        ))}

                                    {/* ── Bullets ── */}
                                    {enabled.bullets && currentResult.bullets?.length > 0 && (
                                        <div
                                            style={{
                                                background: C.surface,
                                                border: `1px solid ${C.border}`,
                                                borderRadius: 10,
                                                overflow: 'hidden',
                                                animation: 'popIn .25s ease .14s both',
                                            }}>
                                            <div
                                                style={{
                                                    padding: '10px 15px',
                                                    borderBottom: `1px solid ${C.border}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                }}>
                                                <span
                                                    style={{
                                                        width: 5,
                                                        height: 5,
                                                        borderRadius: '50%',
                                                        background: C.teal,
                                                        display: 'block',
                                                    }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 500,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '.07em',
                                                        color: C.teal,
                                                    }}>
                                                    Bullets · {currentResult.bullets.length}
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        copy(
                                                            `${currentProduct?.id || 'product'}-bullets-all`,
                                                            currentResult.bullets
                                                                .map((bullet) => `• ${bullet.text}`)
                                                                .join('\n'),
                                                        )
                                                    }
                                                    style={{
                                                        marginLeft: 'auto',
                                                        fontSize: 10,
                                                        padding: '2px 8px',
                                                        borderRadius: 6,
                                                        border: `1px solid ${C.border}`,
                                                        color: C.textSoft,
                                                        background: 'transparent',
                                                        cursor: 'pointer',
                                                    }}>
                                                    {copied[
                                                        `${currentProduct?.id || 'product'}-bullets-all`
                                                    ]
                                                        ? 'copied!'
                                                        : 'copy all'}
                                                </button>
                                            </div>
                                            {currentResult.bullets.map((bullet, index) =>
                                                editingField === `b-${index}` ? (
                                                    <FieldCard
                                                        key={`${currentProduct?.id}-b-${index}`}
                                                        apiKey={apiKey}
                                                        charLimit={0}
                                                        customCommands={customCommands}
                                                        field={{
                                                            id: `gen-b-${index}`,
                                                            type: 'bullet',
                                                            label: bullet.field,
                                                            text: getEditModeText(
                                                                bullet.text,
                                                                currentGenerationHighlights
                                                                    .bullets?.[index] || [],
                                                            ),
                                                            maxChars: fieldCardMaxChars.bullet,
                                                            status:
                                                                resultFieldMeta[`gen-b-${index}`]
                                                                    ?.status || 'draft',
                                                            history:
                                                                resultFieldMeta[`gen-b-${index}`]
                                                                    ?.history || [],
                                                            searchTerms:
                                                                resultFieldMeta[`gen-b-${index}`]
                                                                    ?.searchTerms || [],
                                                        }}
                                                        highlightSubstrings={
                                                            currentGenerationHighlights.bullets?.[
                                                                index
                                                            ] || []
                                                        }
                                                        onDelete={() => setEditingField(null)}
                                                        onOpenChat={() =>
                                                            setChatFieldId((prev) =>
                                                                prev === `gen-b-${index}`
                                                                    ? null
                                                                    : `gen-b-${index}`,
                                                            )
                                                        }
                                                        onRegenerate={regenerateResultField}
                                                        chatOpen={chatFieldId === `gen-b-${index}`}
                                                        onUpdate={handleResultFieldUpdate}
                                                        vars={vars}
                                                    />
                                                ) : (
                                                    <div
                                                        key={index}
                                                        onDoubleClick={() =>
                                                            setEditingField(`b-${index}`)
                                                        }
                                                        title="Double-click to edit"
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 10,
                                                            padding: '11px 15px',
                                                            position: 'relative',
                                                            cursor: 'text',
                                                            borderBottom:
                                                                index <
                                                                currentResult.bullets.length - 1
                                                                    ? `1px solid ${C.border}`
                                                                    : 'none',
                                                            animation: `popIn .2s ease ${0.18 + index * 0.06}s both`,
                                                        }}>
                                                        <div
                                                            style={{
                                                                width: 18,
                                                                height: 18,
                                                                borderRadius: '50%',
                                                                background: C.surfaceRaised,
                                                                border: `1px solid ${C.border}`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 9,
                                                                fontWeight: 500,
                                                                color: C.textFaint,
                                                                flexShrink: 0,
                                                                marginTop: 1,
                                                            }}>
                                                            {index + 1}
                                                        </div>
                                                        <div style={{flex: 1}}>
                                                            <div
                                                                style={{
                                                                    fontSize: 10,
                                                                    fontWeight: 500,
                                                                    color: C.textSoft,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '.06em',
                                                                    marginBottom: 3,
                                                                }}>
                                                                {bullet.field}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize: 13.5,
                                                                    color: C.ink,
                                                                    lineHeight: 1.6,
                                                                    paddingRight: 112,
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                }}>
                                                                <ResolvedText
                                                                    text={bullet.text}
                                                                    vars={vars}
                                                                    highlightSubstrings={
                                                                        currentGenerationHighlights
                                                                            .bullets?.[index] || []
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={actionGroupStyle}>
                                                            <button
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    setEditingField(`b-${index}`);
                                                                }}
                                                                style={actionBtnPrimaryStyle}>
                                                                Edit
                                                            </button>
                                                            <CopyBtn
                                                                inline
                                                                id={`${currentProduct?.id || 'product'}-b-${index}`}
                                                                text={bullet.text}
                                                                copied={copied}
                                                                onCopy={copy}
                                                            />
                                                        </div>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    )}

                                    {/* ── FAQs ──
                                        Same edit-in-place pattern as
                                        bullets: double-click or Edit button
                                        swaps a read-only Q&A pair for a
                                        FieldCard with full tooling. */}
                                    {enabled.faqs && currentResult.faqs?.length > 0 && (
                                        <div
                                            style={{
                                                background: C.surface,
                                                border: `1px solid ${C.border}`,
                                                borderRadius: 10,
                                                overflow: 'hidden',
                                                animation: 'popIn .25s ease .14s both',
                                            }}>
                                            <div
                                                style={{
                                                    padding: '10px 15px',
                                                    borderBottom: `1px solid ${C.border}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                }}>
                                                <span
                                                    style={{
                                                        width: 5,
                                                        height: 5,
                                                        borderRadius: '50%',
                                                        background: C.purple,
                                                        display: 'block',
                                                    }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 500,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '.07em',
                                                        color: C.purple,
                                                    }}>
                                                    FAQs · {currentResult.faqs.length}
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        copy(
                                                            `${currentProduct?.id || 'product'}-faqs-all`,
                                                            currentResult.faqs
                                                                .map(
                                                                    (faq) =>
                                                                        `Q: ${faq.question}\nA: ${faq.answer}`,
                                                                )
                                                                .join('\n\n'),
                                                        )
                                                    }
                                                    style={{
                                                        marginLeft: 'auto',
                                                        fontSize: 10,
                                                        padding: '2px 8px',
                                                        borderRadius: 6,
                                                        border: `1px solid ${C.border}`,
                                                        color: C.textSoft,
                                                        background: 'transparent',
                                                        cursor: 'pointer',
                                                    }}>
                                                    {copied[
                                                        `${currentProduct?.id || 'product'}-faqs-all`
                                                    ]
                                                        ? 'copied!'
                                                        : 'copy all'}
                                                </button>
                                            </div>
                                            {currentResult.faqs.map((faq, index) =>
                                                editingField === `faq-${index}` ? (
                                                    <FieldCard
                                                        key={`${currentProduct?.id}-faq-${index}`}
                                                        apiKey={apiKey}
                                                        charLimit={0}
                                                        customCommands={customCommands}
                                                        field={{
                                                            id: `gen-faq-${index}`,
                                                            type: 'faq',
                                                            label: `FAQ ${index + 1}`,
                                                            question: faq.question || '',
                                                            text: faq.answer || '',
                                                            maxChars: fieldCardMaxChars.faq,
                                                            status:
                                                                resultFieldMeta[`gen-faq-${index}`]
                                                                    ?.status || 'draft',
                                                            history:
                                                                resultFieldMeta[`gen-faq-${index}`]
                                                                    ?.history || [],
                                                            searchTerms:
                                                                resultFieldMeta[`gen-faq-${index}`]
                                                                    ?.searchTerms || [],
                                                        }}
                                                        onDelete={() => setEditingField(null)}
                                                        onOpenChat={() =>
                                                            setChatFieldId((prev) =>
                                                                prev === `gen-faq-${index}`
                                                                    ? null
                                                                    : `gen-faq-${index}`,
                                                            )
                                                        }
                                                        onRegenerate={regenerateResultField}
                                                        chatOpen={chatFieldId === `gen-faq-${index}`}
                                                        onUpdate={handleResultFieldUpdate}
                                                        vars={vars}
                                                    />
                                                ) : (
                                                    <div
                                                        key={index}
                                                        onDoubleClick={() =>
                                                            setEditingField(`faq-${index}`)
                                                        }
                                                        title="Double-click to edit"
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 10,
                                                            padding: '11px 15px',
                                                            position: 'relative',
                                                            cursor: 'text',
                                                            borderBottom:
                                                                index < currentResult.faqs.length - 1
                                                                    ? `1px solid ${C.border}`
                                                                    : 'none',
                                                            animation: `popIn .2s ease ${0.18 + index * 0.06}s both`,
                                                        }}>
                                                        <div
                                                            style={{
                                                                width: 18,
                                                                height: 18,
                                                                borderRadius: '50%',
                                                                background: C.surfaceRaised,
                                                                border: `1px solid ${C.border}`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 9,
                                                                fontWeight: 500,
                                                                color: C.textFaint,
                                                                flexShrink: 0,
                                                                marginTop: 1,
                                                            }}>
                                                            {index + 1}
                                                        </div>
                                                        <div style={{flex: 1, minWidth: 0}}>
                                                            <div
                                                                style={{
                                                                    fontSize: 13.5,
                                                                    fontWeight: 600,
                                                                    color: C.ink,
                                                                    marginBottom: 4,
                                                                    lineHeight: 1.45,
                                                                    paddingRight: 112,
                                                                    wordBreak: 'break-word',
                                                                }}>
                                                                <ResolvedText
                                                                    text={faq.question}
                                                                    vars={vars}
                                                                    highlightSubstrings={
                                                                        currentGenerationHighlights
                                                                            .faqs?.[index] || []
                                                                    }
                                                                />
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize: 13,
                                                                    color: C.ink,
                                                                    lineHeight: 1.6,
                                                                    paddingRight: 112,
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                }}>
                                                                <ResolvedText
                                                                    text={faq.answer}
                                                                    vars={vars}
                                                                    highlightSubstrings={
                                                                        currentGenerationHighlights
                                                                            .faqs?.[index] || []
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={actionGroupStyle}>
                                                            <button
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    setEditingField(`faq-${index}`);
                                                                }}
                                                                style={actionBtnPrimaryStyle}>
                                                                Edit
                                                            </button>
                                                            <CopyBtn
                                                                inline
                                                                id={`${currentProduct?.id || 'product'}-faq-${index}`}
                                                                text={`Q: ${faq.question}\nA: ${faq.answer}`}
                                                                copied={copied}
                                                                onCopy={copy}
                                                            />
                                                        </div>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        {activeChatField && chatListing && (
                            <div
                                style={{
                                    width: 340,
                                    borderLeft: `1px solid ${C.border}`,
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignSelf: 'stretch',
                                }}>
                                <ChatPanel
                                    apiKey={apiKey}
                                    customActions={customActions}
                                    field={activeChatField}
                                    listing={chatListing}
                                    messages={chatHistories[chatFieldId] || []}
                                    onClose={() => setChatFieldId(null)}
                                    onMessage={addChatMessage}
                                    onSetCustomActions={setCustomActions}
                                    onUpdateField={handleResultFieldUpdate}
                                />
                            </div>
                        )}
                    </div>

                    {currentResult && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '10px 1.5rem',
                                borderTop: `1px solid ${C.border}`,
                                background: C.surface,
                                flexShrink: 0,
                            }}>
                            {hasAnyResults && (
                                <GeneratorExportDropdown
                                    count={selectedProducts.filter((p) => resultsByProductId[p.id]).length}
                                    onExport={exportResults}
                                />
                            )}
                            {hasAnyResults && onWorkspaceChange && (
                                <button
                                    onClick={() => onWorkspaceChange('review')}
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        color: '#fff',
                                        background: C.teal,
                                        border: 'none',
                                        padding: '6px 14px',
                                        borderRadius: 7,
                                        cursor: 'pointer',
                                        fontFamily: 'inherit',
                                    }}>
                                    Send to Review →
                                </button>
                            )}
                            <div style={{flex: 1}} />
                            <button
                                onClick={generate}
                                style={{
                                    fontSize: 11.5,
                                    color: C.textMuted,
                                    background: 'transparent',
                                    border: `1px solid ${C.border}`,
                                    padding: '6px 14px',
                                    borderRadius: 7,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                }}>
                                Regenerate all
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ProfileSettingsDialog
                open={profileDialogState.open}
                profile={profileDialogState.profile}
                onClose={closeProfileDialog}
                onSave={handleProfileDialogSave}
            />

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500&family=DM+Sans:wght@300;400;500&display=swap');
                @keyframes popIn{from{opacity:0;transform:translateY(6px) scale(.98)}to{opacity:1;transform:none}}
                @keyframes spin{to{transform:rotate(360deg)}}
                .content-generator-shell button,
                .content-generator-shell input{
                    -webkit-appearance:none;
                    appearance:none;
                    background-image:none;
                }
                .var-input-control::placeholder{
                    color:var(--cg-text-muted);
                    opacity:1;
                }
                .var-input-control:focus{
                    outline:none;
                    border-color:rgba(186,117,23,.58)!important;
                    box-shadow:0 0 0 3px rgba(186,117,23,.12);
                }
                .var-input-control[data-formula="true"]:focus{
                    border-color:rgba(26,122,74,.45)!important;
                    box-shadow:0 0 0 3px rgba(26,122,74,.14);
                }
                .var-input-control:-webkit-autofill{
                    -webkit-text-fill-color:var(--cg-text);
                    -webkit-box-shadow:0 0 0 1000px var(--cg-surface) inset;
                    transition:background-color 9999s ease-out 0s;
                }
                .content-generator-shell{display:grid;grid-template-columns:minmax(420px,460px) minmax(0,1fr)}
                .bullet-editor-grid{display:grid;grid-template-columns:20px minmax(136px,.95fr) minmax(190px,1.35fr) 28px 20px;gap:8px}
                .bullet-editor-heading{font-size:10px;color:var(--cg-text-soft);text-transform:uppercase;letter-spacing:.06em}
                .bullet-editor-heading--ghost{visibility:hidden}
                .bullet-editor-field,.bullet-editor-keywords{min-width:0}
                @media (max-width: 1400px){
                    .content-generator-shell{grid-template-columns:minmax(390px,430px) minmax(0,1fr)}
                    .bullet-editor-grid{grid-template-columns:20px minmax(124px,.9fr) minmax(172px,1.2fr) 28px 20px}
                }
                @media (max-width: 1160px){
                    .content-generator-shell{grid-template-columns:1fr !important}
                    .content-generator-rail-handle{display:none !important}
                    .content-generator-rail{
                        position:relative!important;
                        height:auto!important;
                        max-height:none!important;
                        overflow:visible!important;
                        border-radius:16px 16px 0 0!important;
                        border-right:none!important;
                        border-bottom:1px solid var(--cg-border)!important;
                    }
                    .content-generator-rail-body{
                        overflow:visible!important;
                        padding-bottom:0!important;
                    }
                    .content-generator-rail-footer{
                        position:static!important;
                        left:auto!important;
                        bottom:auto!important;
                        width:auto!important;
                    }
                    .bullet-editor-grid--header{grid-template-columns:20px minmax(0,1fr) 28px 20px;grid-auto-flow:row dense}
                    .bullet-editor-heading--keywords{display:none}
                    .bullet-editor-grid--row{grid-template-columns:20px minmax(0,1fr) 28px 20px;grid-auto-flow:row dense}
                    .bullet-editor-field{grid-column:2/3;grid-row:1}
                    .bullet-editor-keywords{grid-column:2/5;grid-row:2}
                }
                /* Drag-resize narrows the rail independently of viewport
                   width. The first step tightens the wide layout's
                   column minimums so it stays side-by-side at the
                   default ~460px rail; the stack-collapse only kicks in
                   when the rail is genuinely too tight (under ~380px)
                   to render field + keywords usefully.

                   In the stack-collapse mode, grid-auto-flow: row dense
                   plus explicit grid-row on field/keywords keeps the
                   auto-placed wizard and delete buttons in row 1 next
                   to the field — without it they spill to row 3 because
                   keywords (cols 2-4 on row 2) advances the placement
                   cursor past the row-1 gaps. */
                @container (max-width: 520px){
                    .bullet-editor-grid{grid-template-columns:20px minmax(110px,.9fr) minmax(150px,1.2fr) 28px 20px}
                }
                @container (max-width: 380px){
                    .bullet-editor-grid--header{grid-template-columns:20px minmax(0,1fr) 28px 20px;grid-auto-flow:row dense}
                    .bullet-editor-heading--keywords{display:none}
                    .bullet-editor-grid--row{grid-template-columns:20px minmax(0,1fr) 28px 20px;grid-auto-flow:row dense}
                    .bullet-editor-field{grid-column:2/3;grid-row:1}
                    .bullet-editor-keywords{grid-column:2/5;grid-row:2}
                }
                input:focus,select:focus{outline:none;border-color:${C.amber}!important;}
            `}</style>
        </div>
    );
}
